export const blogPosts = [
    {
      id: "Why_Flutter",
      title: "Why I Chose Flutter for Mobile Development",
      type: "tech",
      date: "Janvier 2024",
      summary: "Discover why Flutter became my go-to framework for building cross-platform apps efficiently.",
      fullContent:
      `
      Choosing the right framework for mobile development is a critical decision for any software engineer. After years of experimenting with various technologies such as Java/Kotlin for Android and Swift for iOS, I eventually found a tool that struck the perfect balance between performance, flexibility, and productivity: Flutter.
      
      ## What is Flutter?
      
      Flutter is an open-source UI framework created by Google. It allows developers to build natively compiled applications for mobile, web, and desktop , all from a single codebase. It uses the Dart programming language and provides a rich set of pre-designed widgets, making the development process faster and smoother.
      
      ## Cross-Platform Advantage
      
      The biggest appeal of Flutter is the ability to write one codebase and deploy it on both iOS and Android platforms. In traditional native development, I had to maintain two separate projects, which doubled the time required for updates and bug fixes. Flutter solved that problem elegantly.
      
      ## Performance That Feels Native
      
      Unlike other cross-platform solutions like React Native or Cordova, Flutter doesn’t rely on web views. It compiles directly to native ARM code, which results in high performance, smooth animations, and minimal lag. I tested my apps on both low-end and high-end devices , the results were impressive.
      
      ## Beautiful UI, Faster
      
      Flutter comes with a rich set of Material and Cupertino widgets that adapt to platform design guidelines. I was able to prototype entire screens within hours using just Flutter’s built-in widget library. The “hot reload” feature allowed me to see UI changes instantly, without restarting the app , a huge productivity boost.
      
      ## Community and Ecosystem
      
      Flutter has grown into one of the largest and most active developer communities. With thousands of plugins , I rarely had to reinvent the wheel. Whether it’s Firebase integration, camera access, or payment gateways , there’s a package for almost everything.
      
      ## Scalability and Enterprise Use
      
      Contrary to what many believe, Flutter is not just for small apps or prototypes. Today, large companies like Alibaba, BMW, eBay, and Google Ads are using Flutter for their production apps. The architecture is scalable, and with proper state management (e.g., Provider, Bloc, Riverpod), the codebase remains clean even in large applications.
      
      ## Flutter for Web and Desktop
      
      Another reason I fully adopted Flutter is its growing support for web and desktop applications. While still evolving, Flutter’s multi-platform capabilities open doors for creating a unified application experience across devices , without maintaining multiple tech stacks.
      
      ## Real-World Impact
      
      In my own experience, using Flutter reduced my development time by nearly 40%. Projects that would normally take three months were completed in less than two, without sacrificing quality. Clients were impressed not only by the app performance, but also by the beautiful and modern UI.
      
      ## Conclusion
      
      Flutter is more than a trend , it’s a powerful, production-ready framework that continues to evolve. Its ability to deliver high-quality applications on multiple platforms, while reducing development overhead, makes it a natural choice for both startups and enterprise developers.
      
      If you're a mobile developer looking to boost your productivity and reach a wider audience with less effort, Flutter is absolutely worth considering.
      `
      ,
    },
    {
      id: "Started_with_PostgreSQL",
      title: "Getting Started with PostgreSQL for Beginners",
      type: "tech",
      date: "Janvier 2024",
      summary: "A beginner-friendly guide to setting up and using PostgreSQL in real-world projects.",
      fullContent: `
PostgreSQL is one of the most powerful and reliable open-source relational database systems available today. Whether you're building a web application, a mobile backend, or handling business analytics, PostgreSQL offers the performance, flexibility, and scalability that developers need.

## Why Choose PostgreSQL?

PostgreSQL, often referred to as “Postgres”, is known for its robust feature set. It supports advanced data types, full ACID compliance, powerful indexing options, and extensions like PostGIS for geospatial data. Unlike some database systems that trade off standards compliance for ease of use, Postgres balances both.

Key advantages:
- Open-source and free to use commercially
- Highly extensible with custom data types, functions, and operators
- Excellent performance even with large datasets
- Strong community and rich ecosystem

## Installing PostgreSQL

The installation process is straightforward on most platforms.

### On Ubuntu/Debian:

\'\'\'bash
sudo apt update
sudo apt install postgresql postgresql-contrib
\'\'\'

### On Windows:

Download the installer from the official site [postgresql.org](https://www.postgresql.org/download/windows/) and follow the installation wizard.

## Understanding PostgreSQL Architecture

PostgreSQL runs as a server process and manages:
- A **cluster** of databases
- **Users and roles** with privileges
- Background processes for I/O, autovacuum, write-ahead logging (WAL), and more

Each database is isolated and can contain multiple schemas, tables, indexes, and functions.

## Connecting to PostgreSQL

You can connect using the terminal or GUI tools like **pgAdmin**.

### CLI Example:

\'\'\'bash
sudo -u postgres psql
\'\'\'

You’ll now be inside the PostgreSQL shell (psql), where you can run SQL commands like:

\'\'\'sql
CREATE DATABASE mydb;
\'\'\'

## Basic Commands for Beginners

Here are some essential SQL commands to get started:

### Creating a Table

\'\'\'sql
CREATE TABLE users (
  id SERIAL PRIMARY KEY,
  name VARCHAR(100),
  email TEXT UNIQUE NOT NULL,
  created_at TIMESTAMP DEFAULT NOW()
);
\'\'\'

### Inserting Data

\'\'\'sql
INSERT INTO users (name, email) VALUES ('Raed Madouri', 'raed@example.com');
\'\'\'

### Querying Data

\'\'\'sql
SELECT * FROM users WHERE name ILIKE '%raed%';
\'\'\'

### Updating Records

\'\'\'sql
UPDATE users SET name = 'Raed M.' WHERE id = 1;
\'\'\'

### Deleting Records

\'\'\'sql
DELETE FROM users WHERE id = 1;
\'\'\'

## PostgreSQL with Programming Languages

PostgreSQL has excellent support for integration with languages like:
- **Java** (via JDBC or Spring Boot)
- **Python** (via psycopg2)
- **Node.js** (via pg module)
- **Go** (using pgx or lib/pq)

Here’s a simple example using Node.js:

\'\'\'js
const { Client } = require('pg');
const client = new Client({ connectionString: 'postgres://user:pass@localhost/mydb' });
await client.connect();
const res = await client.query('SELECT * FROM users');
console.log(res.rows);
client.end();
\'\'\'

## Backups and Restore

You should always schedule backups of your PostgreSQL database. Use the following tools:

### Backup:

\'\'\'bash
pg_dump mydb > mydb_backup.sql
\'\'\'

### Restore:

\'\'\'bash
psql mydb < mydb_backup.sql
\'\'\'

For large-scale projects, consider tools like **pgBackRest** or **Barman**.

## Security Best Practices

To keep your database secure:
- Disable remote root access
- Use SSL connections when possible
- Regularly update PostgreSQL
- Limit access to trusted IPs
- Apply strong password policies

## Conclusion

PostgreSQL is an excellent choice for both beginners and professionals. It provides the reliability, performance, and features required for modern applications, without compromising on standards or flexibility. Once you understand the basics of installation, structure, and queries, you’ll find PostgreSQL to be a powerful ally in data management.

If you’re just starting your journey in backend development or looking to switch from another database, PostgreSQL is absolutely worth investing your time in. With a little practice, you’ll master it quickly and confidently build robust systems on top of it.
`
,
    },

    {
        id: "Linux_Server_Security",
        title: "Linux Server Security Best Practices",
        type: "tech",
        date: "Janvier 2024",
        summary: "A comprehensive guide to hardening and securing Linux-based servers for production environments.",
        fullContent: `
      Securing a Linux server is essential in any production environment. While Linux is generally considered more secure than other operating systems, it's far from immune to threats. A poorly configured server can quickly become a target for attackers. In this blog, we’ll explore the most effective practices for protecting your Linux servers from unauthorized access, malware, and data breaches.
      
      ## 1. Keep Your System Updated
      
      One of the simplest but most effective ways to secure your Linux server is to keep the system packages up to date.
      
      Use:
      \'\'\'bash
      sudo apt update && sudo apt upgrade -y    # Debian/Ubuntu
      sudo dnf update                           # Fedora/RHEL
      \'\'\'
      
      Enable automatic security updates:
      \'\'\'bash
      sudo apt install unattended-upgrades
      sudo dpkg-reconfigure --priority=low unattended-upgrades
      \'\'\'
      
      ## 2. Use SSH Keys Instead of Passwords
      
      Passwords can be brute-forced, but SSH keys are far more secure.
      
      - Disable root login via password:
      \'\'\'bash
      sudo nano /etc/ssh/sshd_config
      # PermitRootLogin no
      # PasswordAuthentication no
      \'\'\'
      Then restart SSH:
      \'\'\'bash
      sudo systemctl restart ssh
      \'\'\'
      
      - Generate SSH key pair locally:
      \'\'\'bash
      ssh-keygen -t rsa -b 4096
      \'\'\'
      - Upload your public key to the server:
      \'\'\'bash
      ssh-copy-id user@your-server-ip
      \'\'\'
      
      ## 3. Set Up a Firewall
      
      Use "ufw" (Uncomplicated Firewall) to block unwanted ports:
      
      \'\'\'bash
      sudo ufw default deny incoming
      sudo ufw default allow outgoing
      sudo ufw allow OpenSSH
      sudo ufw enable
      \'\'\'
      
      Verify:
      \'\'\'bash
      sudo ufw status
      \'\'\'
      
      ## 4. Install and Configure Fail2Ban
      
      Fail2Ban protects your server by monitoring logs and banning IPs with multiple failed login attempts.
      
      \'\'\'bash
      sudo apt install fail2ban
      sudo systemctl enable fail2ban
      sudo systemctl start fail2ban
      \'\'\'
      
      Create a local jail file:
      
      \'\'\'bash
      sudo cp /etc/fail2ban/jail.conf /etc/fail2ban/jail.local
      \'\'\'
      
      ## 5. Disable Unused Services and Ports
      
      Check which services are running:
      
      \'\'\'bash
      sudo netstat -tulpn
      \'\'\'
      
      Disable unnecessary services:
      
      \'\'\'bash
      sudo systemctl disable service-name
      \'\'\'
      
      ## 6. Enable Automatic Log Auditing
      
      Use "logwatch" or "auditd" to track system activities:
      
      - Install auditd:
      \'\'\'bash
      sudo apt install auditd audispd-plugins
      \'\'\'
      
      - Start and enable:
      \'\'\'bash
      sudo systemctl enable auditd
      sudo systemctl start auditd
      \'\'\'
      
      ## 7. Set Strong Permissions and Ownership
      
      Make sure sensitive directories and files are not world-readable:
      
      \'\'\'bash
      sudo chmod -R go-rwx /root/
      sudo chmod 600 ~/.ssh/authorized_keys
      \'\'\'
      
      ## 8. Use LUKS or dm-crypt for Disk Encryption
      
      If your server handles sensitive data, encrypt the disk partitions using **LUKS**:
      
      \'\'\'bash
      sudo cryptsetup luksFormat /dev/sdX
      sudo cryptsetup luksOpen /dev/sdX secure_partition
      \'\'\'
      
      ## 9. Monitor Login Sessions
      
      Monitor who is logging in to your server:
      
      \'\'\'bash
      last
      who
      journalctl -xe | grep sshd
      \'\'\'
      
      ## 10. Regular Backups and Snapshotting
      
      Use rsync, borgbackup, or even scheduled "cron" jobs for backups. Store backups on a separate secured system.
      
      ---
      
      ## Conclusion
      
      Security is not a one-time task , it’s a continuous process. By applying these practices, you dramatically reduce your attack surface and protect sensitive data. A secure server ensures trust, reliability, and resilience for your users and clients. Even if you’re a solo developer or a small team, these measures are crucial for any online project.
      
      Invest in security early , it always costs more to fix after an attack than to prevent one.
      `
      },


      {
        id: "Building_REST_APIs_with_Spring_Boot",
        title: "Building REST APIs with Spring Boot: From Zero to Production",
        type: "tech",
        date: "Mars 2024",
        summary: "A complete guide to building robust, scalable RESTful APIs with Spring Boot , from initial setup to production-ready deployment.",
        fullContent: `
      Creating REST APIs with Spring Boot is one of the most powerful ways to build scalable web services in Java. In this post, I'll take you through the complete journey , from creating your first Spring Boot project to deploying a production-ready REST API with proper structure, validation, security, and deployment best practices.
      
      ## 1. Setting Up the Spring Boot Project
      
      I started by generating a project using [Spring Initializr](https://start.spring.io):
      
      - Dependencies:
        - Spring Web
        - Spring Data JPA
        - PostgreSQL Driver
        - Lombok
        - Spring Boot DevTools
      
      Basic structure:
      
      \'\'\'
      src/
       └── main/
           ├── java/
           │   └── com.example.api/
           │       ├── controller/
           │       ├── model/
           │       ├── repository/
           │       ├── service/
           │       └── ApiApplication.java
           └── resources/
               └── application.yml
      \'\'\'
      
      ## 2. Creating the Entity
      
      Here’s a simple entity called "User":
      
      \'\'\'java
      @Entity
      @Data
      public class User {
        @Id
        @GeneratedValue(strategy = GenerationType.IDENTITY)
        private Long id;
      
        private String name;
      
        @Email
        @Column(unique = true)
        private String email;
      }
      \'\'\'
      
      ## 3. Repository Interface
      
      Spring Data JPA handles most queries automatically:
      
      \'\'\'java
      @Repository
      public interface UserRepository extends JpaRepository<User, Long> {
        Optional<User> findByEmail(String email);
      }
      \'\'\'
      
      ## 4. Service Layer (Business Logic)
      
      \'\'\'java
      @Service
      public class UserService {
      
        @Autowired
        private UserRepository userRepository;
      
        public User createUser(User user) {
          return userRepository.save(user);
        }
      
        public List<User> getAllUsers() {
          return userRepository.findAll();
        }
      
        public User getUserById(Long id) {
          return userRepository.findById(id)
            .orElseThrow(() -> new RuntimeException("User not found"));
        }
      }
      \'\'\'
      
      ## 5. REST Controller
      
      \'\'\'java
      @RestController
      @RequestMapping("/api/users")
      public class UserController {
      
        @Autowired
        private UserService userService;
      
        @PostMapping
        public ResponseEntity<User> create(@RequestBody @Valid User user) {
          return new ResponseEntity<>(userService.createUser(user), HttpStatus.CREATED);
        }
      
        @GetMapping
        public List<User> list() {
          return userService.getAllUsers();
        }
      
        @GetMapping("/{id}")
        public ResponseEntity<User> get(@PathVariable Long id) {
          return ResponseEntity.ok(userService.getUserById(id));
        }
      }
      \'\'\'
      
      ## 6. Input Validation
      
      Add validation annotations ("@NotNull", "@Email", "@Size", etc.) in your entity and handle them globally:
      
      \'\'\'java
      @ControllerAdvice
      public class GlobalExceptionHandler {
      
        @ExceptionHandler(MethodArgumentNotValidException.class)
        public ResponseEntity<String> handleValidation(MethodArgumentNotValidException ex) {
          return ResponseEntity.badRequest().body("Invalid input: " + ex.getMessage());
        }
      }
      \'\'\'
      
      ## 7. API Documentation with Swagger
      
      Add dependency:
      
      \'\'\'xml
      <dependency>
        <groupId>org.springdoc</groupId>
        <artifactId>springdoc-openapi-ui</artifactId>
        <version>1.6.15</version>
      </dependency>
      \'\'\'
      
      Then go to "http://localhost:8080/swagger-ui.html" to explore your live API.
      
      ## 8. Security (JWT)
      
      For production, I implemented authentication using JWT:
      
      - Login endpoint returns JWT token
      - All other endpoints require a valid token in "Authorization" header
      - Token filter extracts and verifies token for every request
      
      This ensures secure, stateless authentication.
      
      ## 9. Dockerizing the API
      
      I created a Dockerfile:
      
      \'\'\'dockerfile
      FROM openjdk:17-alpine
      COPY target/api.jar api.jar
      ENTRYPOINT ["java", "-jar", "api.jar"]
      \'\'\'
      
      Then ran:
      
      \'\'\'bash
      docker build -t spring-api .
      docker run -p 8080:8080 spring-api
      \'\'\'
      
      ## 10. Deploying to Production (VPS)
      
      I deployed it to a VPS (Ubuntu) with:
      - Nginx as reverse proxy
      - SSL (Let's Encrypt)
      - PostgreSQL hosted on server
      - UFW firewall configured
      
      ---
      
      ## Final Thoughts
      
      Spring Boot makes it incredibly efficient to build REST APIs in Java. With proper layering, validation, and deployment practices, you can build APIs that are scalable, secure, and maintainable. This project taught me how important structure, automation, and testing are in delivering production-ready services , and I now apply those principles across all my backend work.
      `
      },
      

      {
        id: "Offline Storage in Flutter",
        title: "Offline Storage in Flutter: SQLite vs Hive",
        type: "tech",
        date: "Mars 2024",
        summary: "A detailed comparison of SQLite and Hive for offline data storage in Flutter apps, including use cases, pros, cons, and performance insights.",
        fullContent: `
      In mobile development, offline storage is often a necessity , especially in apps that deal with user data, caching, or operate in low-connectivity environments. Flutter offers several options for local storage, but two of the most popular are **SQLite** and **Hive**.
      
      In this article, I'll share my experience comparing both technologies and guide you through when to use each one.
      
      ---
      
      ## 1. What is SQLite?
      
      SQLite is a lightweight, relational database that uses SQL syntax. It’s a well-known database used widely in Android development and is ideal for structured data.
      
      - Plugin: \'sqflite\'
      - Structure: Tables, rows, foreign keys
      - Query Language: SQL
      
      ### When to Use SQLite:
      - You need relational data
      - You want to run complex queries (joins, conditions)
      - You’re building large-scale apps with structured data
      
      ---
      
      ## 2. What is Hive?
      
      Hive is a lightweight, NoSQL database written in pure Dart. It’s super fast and doesn’t require any native dependencies.
      
      - Plugin: \'hive\'
      - Structure: Boxes (key-value)
      - Query Language: None (programmatic access)
      
      ### When to Use Hive:
      - You need very fast reads/writes
      - Your data is simple or non-relational
      - You want to avoid platform-specific issues
      
      ---
      
      ## 3. Performance Comparison
      
      I tested both on the same Flutter app with the following operations:
      - Insert 1000 records
      - Read all records
      - Update 1000 records
      - Delete 1000 records
      
      | Operation | SQLite (ms) | Hive (ms) |
      |----------|--------------|------------|
      | Insert   | 1400         | 300        |
      | Read     | 500          | 80         |
      | Update   | 1100         | 350        |
      | Delete   | 900          | 200        |
      
      ✅ **Hive is significantly faster**, especially for reads.
      
      ❗ But SQLite handles data integrity and structure better.
      
      ---
      
      ## 4. SQLite Example
      
      \'\'\'dart
      final db = await openDatabase('my.db');
      await db.insert('users', {'name': 'Raed', 'email': 'raed@example.com'});
      
      final users = await db.query('users');
      \'\'\'
      
      ---
      
      ## 5. Hive Example
      
      \'\'\'dart
      var box = await Hive.openBox('users');
      box.put('raed', {'email': 'raed@example.com'});
      
      print(box.get('raed'));
      \'\'\'
      
      Hive requires data to be serializable, and often uses adapters:
      
      \'\'\'dart
      class User extends HiveObject {
        String name;
        String email;
      }
      \'\'\'
      
      ---
      
      ## 6. Pros and Cons
      
      | Feature             | SQLite                   | Hive                     |
      |---------------------|--------------------------|--------------------------|
      | Relational Queries  | ✅ Yes                   | ❌ No                    |
      | Speed               | 🟡 Medium                | ✅ Fast                  |
      | Structured Schema   | ✅ Required              | ❌ Dynamic               |
      | Platform Stability  | ✅ Mature                | 🟡 Good (Dart-only)      |
      | Encryption Support  | ✅ (via plugin)          | ✅ (built-in option)     |
      
      ---
      
      ## 7. Best Use Cases
      
      **Use SQLite when:**
      - You need normalized data and relations
      - You work with existing SQL-based backend
      - Your app handles reports, analytics, or search queries
      
      **Use Hive when:**
      - You prioritize speed and simplicity
      - You need fast local caching
      - You store key-value data or preferences
      
      ---
      
      ## 8. My Personal Recommendation
      
      In my own apps, I use:
      - **SQLite** for large apps with complex data (ex: contact manager, todo apps with tagging)
      - **Hive** for fast caching, preferences, and simpler data (ex: authentication tokens, theme settings)
      
      If you're building a hybrid use case, you can even combine both in one app , using Hive for cache and SQLite for structured records.
      
      ---
      
      ## Conclusion
      
      Both SQLite and Hive are powerful in their own way. Your choice depends on your project needs , do you need structure and relationships (go SQLite), or speed and simplicity (go Hive)? Understanding their strengths helps you build efficient, maintainable, and scalable Flutter apps.
      
      Try both in a test project and measure what works best for you.
      `
      },

      
      {
        id: "HTTPS_and_SSL_Certificates",
        title: "How I Configured HTTPS and SSL Certificates for a Web Server",
        type: "tech",
        date: "Mai 2024",
        summary: "A practical guide to securing your web server with HTTPS and free SSL certificates using Let's Encrypt and Nginx.",
        fullContent: `
      In 2025, having a website without HTTPS is unacceptable. Not only does it reduce user trust, but many browsers block or flag non-HTTPS content. In this article, I'll walk you through how I secured my own web server using free SSL certificates from Let’s Encrypt, and how I configured it on Nginx.
      
      ---
      
      ## 1. Why HTTPS Matters
      
      HTTPS ensures that:
      - Data is encrypted during transmission
      - Users are protected from man-in-the-middle attacks
      - Browsers show a secure lock icon
      - Google ranks your site higher in search results
      
      ---
      
      ## 2. Choosing the Right Server Stack
      
      I used the following setup:
      - **Ubuntu Server 22.04**
      - **Nginx** as the web server
      - **Let’s Encrypt** with Certbot
      
      This stack is lightweight, efficient, and works perfectly for hosting Spring Boot apps, static sites, or APIs.
      
      ---
      
      ## 3. Installing Nginx
      
      First, I installed and enabled Nginx:
      
      \'\'\'bash
      sudo apt update
      sudo apt install nginx
      sudo systemctl enable nginx
      sudo systemctl start nginx
      \'\'\'
      
      Then I verified:
      \'\'\'bash
      sudo ufw allow 'Nginx Full'
      sudo ufw status
      \'\'\'
      
      ---
      
      ## 4. Setting Up a Domain
      
      I purchased a domain and pointed the DNS A record to my VPS public IP address.
      
      Example:
      - Domain: \'myportfolio.dev\'
      - IP: \'198.51.100.42\'
      
      I verified it with:
      \'\'\'bash
      ping myportfolio.dev
      \'\'\'
      
      ---
      
      ## 5. Installing Certbot (Let’s Encrypt)
      
      Let’s Encrypt provides **free SSL certificates** for any domain. I installed Certbot like this:
      
      \'\'\'bash
      sudo apt install certbot python3-certbot-nginx
      \'\'\'
      
      Then I requested an SSL certificate:
      
      \'\'\'bash
      sudo certbot --nginx -d myportfolio.dev -d www.myportfolio.dev
      \'\'\'
      
      Certbot:
      - Auto-generated the certificate
      - Configured Nginx
      - Reloaded the server
      
      ---
      
      ## 6. Automatic Renewal
      
      Let’s Encrypt certs expire every 90 days. Certbot installs a cron job automatically:
      
      You can test it with:
      \'\'\'bash
      sudo certbot renew --dry-run
      \'\'\'
      
      This ensures your HTTPS never expires silently.
      
      ---
      
      ## 7. Custom Nginx Configuration
      
      Here's a simple Nginx config for my site:
      
      \'\'\'
      server {
          listen 80;
          server_name myportfolio.dev www.myportfolio.dev;
          return 301 https://\$host\$request_uri;
      }
      
      server {
          listen 443 ssl;
          server_name myportfolio.dev www.myportfolio.dev;
      
          ssl_certificate /etc/letsencrypt/live/myportfolio.dev/fullchain.pem;
          ssl_certificate_key /etc/letsencrypt/live/myportfolio.dev/privkey.pem;
      
          location / {
              proxy_pass http://localhost:8080; # if backend runs locally
              proxy_set_header Host \$host;
              proxy_set_header X-Real-IP \$remote_addr;
              proxy_set_header X-Forwarded-For \$proxy_add_x_forwarded_for;
              proxy_set_header X-Forwarded-Proto \$scheme;
          }
      }
      \'\'\'
      
      ---
      
      ## 8. Troubleshooting Tips
      
      - Make sure port 80 and 443 are open:
        \'\'\'bash
        sudo ufw allow 443
        \'\'\'
      - DNS must point to your IP **before** using Certbot
      - You can manually reload Nginx after editing:
        \'\'\'bash
        sudo nginx -t && sudo systemctl reload nginx
        \'\'\'
      
      ---
      
      ## 9. Securing Other Services (Optional)
      
      I also secured:
      - Spring Boot backend APIs
      - Static dashboards
      - Admin panels
      
      For Spring Boot apps, enable HTTPS by creating a keystore and setting properties:
      \'\'\'properties
      server.port=8443
      server.ssl.key-store=classpath:keystore.p12
      server.ssl.key-store-password=yourpass
      server.ssl.key-store-type=PKCS12
      server.ssl.key-alias=tomcat
      \'\'\'
      
      ---
      
      ## Conclusion
      
      Securing your web server with HTTPS using Let's Encrypt is fast, free, and critical for user trust. Once done, your app or site looks professional, avoids browser security warnings, and respects data privacy.
      
      Whether you're deploying a portfolio, a web API, or a business app , make HTTPS your default. You’ll never deploy without it again.
      `
      },

      {
        id: "Management",
        title: "How I Manage Side Projects While Working Full-Time",
        type: "tech",
        date: "Mai 2024",
        summary: "A personal look at how I balance a full-time job with building meaningful side projects , and the mindset, tools, and habits that make it possible.",
        fullContent: `
      Working full-time as a developer is already a mentally demanding task. So how can someone still find the time and energy to build side projects, launch apps, write blogs, or experiment with new technologies?
      
      In this post, I’ll share my personal journey, strategies, and routines that help me manage side projects while working a 9-to-6 job.
      
      ---
      
      ## 1. Why I Build Side Projects
      
      Side projects have been more than just hobbies for me. They:
      - Help me learn new tools and frameworks (e.g., Flutter, PostGIS, Spring Boot)
      - Serve as a personal creative outlet
      - Boost my portfolio and visibility
      - Teach me how to take ownership from idea to deployment
      
      Many of my skills today didn’t come from my full-time job , they came from my late-night or weekend experiments.
      
      ---
      
      ## 2. Choosing the Right Projects
      
      I used to start 10 projects and finish none. Now, I follow a simple rule:
      
      > **Build something small and useful.**
      
      Some filters I use:
      - Can I complete an MVP in 1–2 weeks?
      - Does it solve a real problem I care about?
      - Can I learn one new thing from it?
      
      Examples:
      - A Flutter app to simulate TELC exams
      - A Spring Boot API to manage my servers
      - A blog system to organize my portfolio content
      
      ---
      
      ## 3. Blocking Time and Energy
      
      I treat side projects like mini freelance clients.
      
      - **Weekdays:** 1–2 hours in the evening
      - **Weekends:** Deep focus blocks (2–4 hours)
      - **Breaks at work:** Research or planning (without coding)
      
      More important than time is **energy management**. I don’t force work when I’m exhausted , instead, I plan ahead and do small, consistent progress.
      
      ---
      
      ## 4. Building Systems, Not Tasks
      
      I automate everything I can:
      - GitHub Actions for CI/CD
      - Cron jobs for backups
      - Markdown files for blogging
      - Boilerplate templates for Flutter/Spring projects
      
      This saves me hours of setup and lets me focus on what matters: building and shipping.
      
      ---
      
      ## 5. Prioritizing Ruthlessly
      
      When you only have 8–10 hours per week, **prioritization is everything**.
      
      I use:
      - **Trello/Notion** to track tasks
      - The 80/20 rule: build 20% of the features that bring 80% of the value
      - A “cut list”: features I postpone or skip
      
      I focus on momentum over perfection.
      
      ---
      
      ## 6. Avoiding Burnout
      
      I’ve hit burnout before. Now I:
      - Take breaks without guilt
      - Switch to passive tasks (reading docs, UI design)
      - Celebrate small wins
      - Share progress publicly to stay motivated
      
      Passion should fuel your creativity , not drain it.
      
      ---
      
      ## 7. Publishing and Showcasing
      
      Every time I complete something, I:
      - Write a blog post about it
      - Share screenshots or demos on GitHub
      - Add it to my portfolio website
      - Reflect on what I learned
      
      This transforms effort into long-term value. Even small projects build trust with employers, clients, or collaborators.
      
      ---
      
      ## Conclusion
      
      Balancing side projects with a full-time job isn’t about working more , it’s about working smarter, with intention and love for what you’re building. The freedom to create on your own terms is priceless.
      
      If you're working full-time but feel the urge to build something of your own , start small, start now. You’ll be amazed what you can create in just a few hours a week.
      `
      },
      
      {
        "id": "Securing_Spring_Boot",
        "title": "Securing Your Spring Boot Application with JWT and Role-Based Access: A Deep Dive",
        "type": "tech",
        "date": "Mai 2024",
        "summary": "A deep dive into securing Spring Boot applications using JSON Web Tokens (JWT) and Role-Based Access Control (RBAC), with advanced implementation tips and real-world security best practices.",
        "fullContent": `In the contemporary digital ecosystem, the imperative to secure applications against unauthorized access and data breaches cannot be overstated. Spring Boot, renowned for its rapid development capabilities, also offers a robust framework for implementing sophisticated security measures. This article transcends a basic overview, offering a detailed examination of how to fortify your Spring Boot application by seamlessly integrating the stateless authentication mechanism of JSON Web Tokens (JWT) with a meticulously designed Role-Based Access Control (RBAC) strategy. Our focus will extend beyond conventional implementations, delving into nuanced aspects and practical considerations often overlooked.
      
      ### Beyond Basic Authentication: Embracing Statelessness with JWT
      
      Traditional session-based authentication often introduces scalability challenges in distributed systems. JWT offers an elegant alternative by enabling stateless authentication. Once a user successfully authenticates, the server issues a self-contained JWT that the client can then present with subsequent requests. The server can verify the token's authenticity and integrity without relying on server-side session storage.
      
      **Deconstructing the JWT: Header, Payload, and Signature in Detail:**
      
      1. **Header:** Typically encodes the signing algorithm (e.g., HMAC SHA-256 - HS256, or RSA SHA-256 - RS256) and the token type (e.g., "JWT").
      2. **Payload (Claims):** This JSON object carries crucial information, such as \`iss\`, \`aud\`, \`exp\`, etc. Avoid including sensitive data.
      3. **Signature:** Ensures the token hasn’t been tampered with. Created using the secret/private key and a hashing algorithm.
      
      **Advanced JWT Implementation Strategies in Spring Boot:**
      - Token Revocation
      - Refresh Tokens
      - Custom Claims
      - JWT in Microservices
      
      ### Granular Access Control with Role-Based Access (RBAC): Beyond Simple Role Checks
      
      RBAC simplifies managing permissions. Define roles → map permissions → assign to users.
      
      **Integrating RBAC with Spring Security:**
      - \`UserDetailsService\`
      - \`@PreAuthorize\`, \`@PostAuthorize\`, and \`HttpSecurity\`
      - \`@EnableMethodSecurity\`
      - \`AccessDecisionVoter\` or custom \`PermissionEvaluator\`
      
      ### Best Practices:
      - Secure JWT storage (HTTP-only cookies or secure local storage)
      - CORS configuration
      - CSRF/XSS protection
      - Logging and monitoring
      - Regular audits
      - Principle of least privilege
      
      Securing your Spring Boot app with JWT and RBAC is essential to protecting your users and your data in today’s landscape.`
      }
,      

{
    "id": "Building_Dashboards",
    "title": "Unifying Admin Experiences: Building Dashboards for Web and Mobile with Flutter",
    "type": "tech",
    "date": "juin 2024",
    "summary": "Explore how Flutter enables unified admin dashboards across web and mobile platforms from a single codebase. This guide covers architecture, responsive layouts, state management, and UI/UX adaptation strategies.",
    "fullContent": `In today's multi-device world, providing a consistent and efficient administrative interface across web and mobile platforms is increasingly crucial. Flutter, Google's UI toolkit, offers a compelling solution by enabling developers to build natively compiled applications for web, mobile (Android and iOS), and desktop from a single codebase. This article explores the strategic advantages and practical considerations of leveraging Flutter to construct unified admin dashboards that deliver a seamless experience regardless of the access device.
  
  ### The Strategic Advantage: Single Codebase, Multiple Platforms
  
  The primary allure of using Flutter for admin dashboards lies in its promise of code reuse. Developing and maintaining separate codebases for web and mobile can be resource-intensive and lead to inconsistencies in UI/UX and functionality. Flutter's "write once, run anywhere" capability significantly reduces development time, effort, and the potential for platform-specific bugs. This allows development teams to focus on core features and deliver updates more efficiently across all supported platforms.
  
  Furthermore, Flutter's rich set of pre-built widgets and its expressive declarative UI framework facilitate rapid development of visually appealing and highly customizable interfaces. This accelerates the process of building complex dashboard layouts and interactive components essential for effective administration.
  
  ### Architectural Considerations for Cross-Platform Admin Dashboards
  
  While Flutter enables code sharing, designing a truly unified admin dashboard requires careful architectural planning:
  
  1. **Responsive Layout Design:** The key to a seamless experience across different screen sizes is a well-thought-out responsive layout. Flutter's \'LayoutBuilder\', \'MediaQuery\', and flexible widgets like \'Expanded\', \'Flexible\', and \'GridView\' are invaluable for creating UIs that adapt gracefully to various screen dimensions.
  
  2. **Navigation Strategies:** Navigation patterns differ significantly between web and mobile. Web dashboards typically employ sidebars or top navigation bars, while mobile apps often rely on bottom navigation bars or drawers. Use \'kIsWeb\' to adjust rendering by platform.
  
  3. **Data Management and State Management:** Use Provider, Riverpod, BLoC, Cubit, or GetX based on your app's scale and complexity. Keep data/business logic platform-independent.
  
  4. **API Integration:** Use \'http\' or \'dio\' for backend communication. Abstract your API calls for easier maintenance.
  
  5. **Plugin Ecosystem:** Be cautious with plugins. Use platform checks with \'kIsWeb\' or platform channels if needed.
  
  ### UI/UX Adaptations for Web and Mobile
  
  - **Web:** Focus on information density, hover interactions, and keyboard navigation.
  - **Mobile:** Optimize for touch interactions, performance, and simplified user flows.
  
  Use adaptive widgets or conditional rendering to serve the right experience per platform.
  
  ### Practical Strategies and Tools
  
  - \'responsive_builder\', \'flutter_screenutil\'
  - UI kits / design systems / FlutterFlow
  - Flutter CI/CD with Codemagic or GitHub Actions
  - Cross-platform testing with widget and integration tests
  - Performance profiling and code splitting
  
  ### The Future of Unified Admin Experiences with Flutter
  
  Flutter's maturity across web and mobile makes it a serious option for building admin interfaces. Developers can deliver cohesive, powerful tools while maintaining a single codebase, cutting costs, and improving time-to-market.
  `,
  }
,  

{
    id: "Mastering_Responsive_UI_in_Flutter",
    title: "Mastering Responsive UI in Flutter: Best Practices with MediaQuery and LayoutBuilder",
    type: "tech",
    date: "juillet 2024",
    summary: "A comprehensive guide to building adaptive Flutter UIs that respond to different screen sizes using MediaQuery and LayoutBuilder, covering practical patterns and scalable design strategies.",
    fullContent: `In the diverse landscape of modern devices, creating user interfaces that look and function flawlessly across a multitude of screen sizes and orientations is paramount. Flutter, with its rich widget library and powerful layout system, provides the necessary tools to build truly responsive UIs. Among these tools, \'MediaQuery\' and \'LayoutBuilder\' stand out as fundamental building blocks for achieving adaptability. This article delves into best practices for effectively utilizing these widgets to construct maintainable and scalable responsive Flutter applications.
  
  ### Understanding the Core Concepts: MediaQuery and LayoutBuilder
  
  Before diving into best practices, let's briefly recap the purpose of these essential widgets:
  
  - **MediaQuery:** Provides access to information about the current device's display characteristics, such as screen size (width, height), orientation, pixel density, platform brightness, and more.
  - **LayoutBuilder:** Offers the dimensions of its parent widget's available space as constraints. This allows you to build layouts that adapt based on the space provided by the parent.
  
  ### Best Practices for Responsive UI with MediaQuery
  
  1. **Strategic Use of Screen Dimensions:** Use \'MediaQuery.of(context).size.width\' or \'.height\' to control layout and widget sizing.
  2. **Handling Orientation Changes:** Use \'MediaQuery.of(context).orientation\' or \'OrientationBuilder\' to dynamically adapt layout based on screen rotation.
  3. **Leveraging Aspect Ratio:** Access \'MediaQuery.of(context).size.aspectRatio\' to preserve consistent proportions across screen sizes.
  4. **Adapting Font Sizes and Spacing:** Instead of hardcoding values, derive font sizes and padding from the screen width for scalable designs.
  5. **Device Pixel Ratio (DPR):** Use \'MediaQuery.of(context).devicePixelRatio\' when implementing custom rendering for sharp visuals.
  6. **Global Responsive Theme:** Define reusable breakpoints and utility methods to simplify responsive adjustments throughout your app.
  
  ### Best Practices for Responsive UI with LayoutBuilder
  
  1. **Reusable Responsive Components:** Encapsulate internal layout responsiveness within widgets using \'LayoutBuilder\'.
  2. **Adaptive Grids and Lists:** Use the \'maxWidth\' from \'BoxConstraints\' to dynamically determine column counts in grids.
  3. **Breakpoint-Specific Layouts:** Wrap parts of your UI in \'LayoutBuilder\' and apply different structures depending on the available space.
  4. **Combine with MediaQuery:** Use both together for hybrid control,\'LayoutBuilder\' for local constraints, \'MediaQuery\' for device-wide metrics.
  5. **Performance Tips:** Avoid placing expensive computation or rebuild logic directly inside the builder callback.
  
  ### General Best Practices for Responsive UI in Flutter
  
  1. **Mobile-First Design:** Start from the smallest screen and progressively enhance layouts for larger displays.
  2. **Use Flexible Widgets:** Take advantage of \'Expanded\', \'Flexible\', \'Wrap\', and \'Stack\' to build elastic layouts.
  3. **Avoid Hardcoded Values:** Define dimensions, padding, and text size in proportion to screen size using responsive utilities.
  4. **Test Across Devices:** Test your layout on a range of devices and orientations using emulators and real phones.
  5. **Iterate and Refine:** Gather user feedback and iterate on spacing, hierarchy, and interactions.
  6. **Follow UI Guidelines:** Respect platform design systems such as Material Design for Android and Human Interface Guidelines for iOS.
  
  By thoughtfully applying these best practices and effectively utilizing \'MediaQuery\' and \'LayoutBuilder\', you can create Flutter applications with truly responsive user interfaces that adapt seamlessly to any screen, providing an optimal experience for all your users.
  `
  }
  ,
      
  {
    id: "OpenLayers_GeoServer",
    title: "Visualizing the Earth Beneath: Integrating Soil Maps with OpenLayers and GeoServer",
    type: "tech",
    date: "juillet 2024",
    summary: "A practical guide on how to serve and visualize geospatial soil data using GeoServer and OpenLayers, covering formats, styling, and interactivity in the browser.",
    fullContent: `
  Understanding and visualizing soil data is crucial for applications such as agriculture, land planning, and environmental monitoring. In this guide, we’ll integrate GeoServer for serving spatial data and OpenLayers for rendering it interactively in the browser.
  
  ### 1. Preparing Soil Map Data
  
  **Supported formats:**
  - Shapefiles (.shp, .shx, .dbf, .prj)
  - GeoTIFF (.tif)
  - GeoJSON (.geojson)
  - PostGIS (PostgreSQL with spatial extension)
  
  **Preparation steps:**
  - Ensure data is projected in EPSG:4326 (WGS 84).
  - Include meaningful attribute data (e.g., soil_type, pH, moisture).
  - Clean attribute names (no spaces, consistent casing).
  
  ### 2. Publishing Soil Layers with GeoServer
  
  1. **Install GeoServer** and login to the admin console.
  2. **Create a workspace** named 'soil_data'.
  3. **Add a store**:
     - Shapefile → upload the full set
     - GeoTIFF → upload '.tif' file
     - PostGIS → enter database credentials
  4. **Publish a layer**:
     - Set Layer Name: e.g., 'soil_types'
     - Confirm bounding box and CRS (EPSG:4326)
  5. **Style it using SLD**:
     - Create SLD file and upload (e.g., color-coded soil types)
     - Example rule:
  
  \'\'\'xml
  <Rule>
    <Name>Clay</Name>
    <Title>Clay Soil</Title>
    <ogc:Filter>
      <ogc:PropertyIsEqualTo>
        <ogc:PropertyName>soil_type</ogc:PropertyName>
        <ogc:Literal>clay</ogc:Literal>
      </ogc:PropertyIsEqualTo>
    </ogc:Filter>
    <PolygonSymbolizer>
      <Fill>
        <CssParameter name="fill">#b5651d</CssParameter>
      </Fill>
    </PolygonSymbolizer>
  </Rule>
  \'\'\'
  
  6. **Preview it via WMS preview tab.**
  
  ### 3. Displaying Soil Maps with OpenLayers
  
  **HTML & JS Setup:**
  
  \'\'\'html
  <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8">
    <title>Soil Map Viewer</title>
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/ol@v7.1.0/ol.css" type="text/css">
    <style>#map { width: 100%; height: 500px; }</style>
  </head>
  <body>
    <div id="map"></div>
    <div id="info"></div>
    <script src="https://cdn.jsdelivr.net/npm/ol@v7.1.0/ol.js"></script>
    <script>
      const map = new ol.Map({
        target: 'map',
        layers: [
          new ol.layer.Tile({
            source: new ol.source.OSM()
          })
        ],
        view: new ol.View({
          center: ol.proj.fromLonLat([10.18, 36.8]), // Example for Tunisia
          zoom: 7
        })
      });
  
      const soilLayer = new ol.layer.Tile({
        source: new ol.source.TileWMS({
          url: 'https://yourdomain.com/geoserver/soil_data/wms',
          params: {
            'LAYERS': 'soil_data:soil_types',
            'TILED': true,
            'STYLES': 'soil_style',
            'FORMAT': 'image/png'
          },
          serverType: 'geoserver'
        })
      });
      map.addLayer(soilLayer);
  
      // Optional: Feature Info
      map.on('singleclick', function(evt) {
        const viewResolution = map.getView().getResolution();
        const url = soilLayer.getSource().getFeatureInfoUrl(
          evt.coordinate,
          viewResolution,
          'EPSG:4326',
          { 'INFO_FORMAT': 'text/html' }
        );
        if (url) {
          fetch(url)
            .then(res => res.text())
            .then(html => {
              document.getElementById('info').innerHTML = html;
            });
        }
      });
    </script>
  </body>
  </html>
  \'\'\'
  
  ### Conclusion
  
  By combining GeoServer and OpenLayers, you can publish and visualize rich, interactive soil data accessible via web. This stack is scalable, customizable, and open-source,ideal for government agencies, researchers, or startups in agri-tech. For further enhancements, consider caching strategies, interactive legends, and advanced filters using WFS and custom queries.
  `,
    keywords: "GeoServer OpenLayers soil maps tutorial, WMS layer setup, GeoTIFF shapefile GeoJSON, soil visualization web, EPSG:4326 spatial data, SLD styling GeoServer, how to publish shapefiles, PostGIS spatial layer publish, OpenLayers WMS map example, interactive soil maps browser"
  }
  ,
      
      {
        "id": "Monitoring_Server",
        "title": "Monitoring Server Uptime with Bash Scripts and Email Alerts",
        "type": "tech",
        "date": "septembre 2024",
        "summary": "Learn how to implement a lightweight and reliable system to monitor server uptime using Bash scripts combined with automatic email alerts to notify you of downtime or failures.",
        "fullContent": `
      Maintaining high availability for production servers is critical for businesses and developers. Whether you're managing web applications, databases, or internal tools, you need to be immediately aware when a server goes down. In this guide, we'll explore how to create a simple yet powerful server monitoring system using Bash scripts and email notifications.
      
      ### Why Use Bash for Server Monitoring?
      
      Bash is available on most Linux systems by default, and it's lightweight, customizable, and scriptable. When paired with email tools like \'mailx\' or \'msmtp\', it becomes a powerful way to monitor and respond to server outages in real-time without needing third-party services.
      
      ---
      
      ### 1. Basic Uptime Check with Ping
      
      The most straightforward way to monitor uptime is to ping the server periodically.
      
      \'\'\'bash
      #!/bin/bash
      
      SERVER="8.8.8.8"
      LOGFILE="/var/log/server_uptime.log"
      TIMESTAMP=$(date "+%Y-%m-%d %H:%M:%S")
      
      ping -c 2 $SERVER > /dev/null
      
      if [ $? -ne 0 ]; then
          echo "$TIMESTAMP - Server $SERVER is DOWN" >> $LOGFILE
      else
          echo "$TIMESTAMP - Server $SERVER is UP" >> $LOGFILE
      fi
      \'\'\'
      
      This script will check if the server responds and append the result to a log file.
      
      ---
      
      ### 2. Adding Email Notifications
      
      Let’s improve this by adding email alerts. Make sure \'mailx\' or \'msmtp\' is installed and configured.
      
      \'\'\'bash
      #!/bin/bash
      
      SERVER="8.8.8.8"
      EMAIL="admin@example.com"
      SUBJECT="🔴 Server DOWN: $SERVER"
      MESSAGE="Alert: The server $SERVER is not reachable as of $(date)"
      LOGFILE="/var/log/server_uptime.log"
      
      ping -c 2 $SERVER > /dev/null
      
      if [ $? -ne 0 ]; then
          echo "$MESSAGE" | mail -s "$SUBJECT" "$EMAIL"
          echo "$(date) - $SERVER is DOWN" >> $LOGFILE
      else
          echo "$(date) - $SERVER is UP" >> $LOGFILE
      fi
      \'\'\'
      
      ---
      
      ### 3. Automating the Check with Cron
      
      To automate this check every 5 minutes, add the script to your crontab:
      
      \'\'\'bash
      */5 * * * * /path/to/monitor.sh
      \'\'\'
      
      You can use \'crontab -e\' to edit the cron jobs.
      
      ---
      
      ### 4. Monitoring Multiple Servers
      
      Modify the script to loop through multiple servers:
      
      \'\'\'bash
      SERVERS=("8.8.8.8" "1.1.1.1" "example.com")
      EMAIL="admin@example.com"
      
      for SERVER in "\${SERVERS[@]}"; do
        ping -c 2 $SERVER > /dev/null
        if [ $? -ne 0 ]; then
          echo "Server $SERVER is DOWN at $(date)" | mail -s "🔴 $SERVER DOWN" "$EMAIL"
        fi
      done
      \'\'\'
      
      ---
      
      ### 5. Advanced: Using curl for HTTP-based Services
      
      You can also check the HTTP response for web servers:
      
      \'\'\'bash
      URL="https://yourdomain.com"
      STATUS=$(curl -s -o /dev/null -w "%{http_code}" $URL)
      
      if [ "$STATUS" -ne 200 ]; then
          echo "Website $URL is DOWN - HTTP $STATUS" | mail -s "🚨 Website Down" "$EMAIL"
      fi
      \'\'\'
      
      ---
      
      ### Conclusion
      
      By combining Bash with standard Unix tools like \'ping\', \'curl\', and \'mailx\', you can build a robust and efficient uptime monitoring system that fits into any Linux server setup. This lightweight approach is perfect for self-managed environments and offers full control without the overhead of third-party monitoring platforms.
      
      Make sure your SMTP configuration is tested and secure, especially when sending alerts to external email addresses.
        `
      },
      
      {
        "id": "Deploy_React_App_on_Debian",
        "title": "How to Deploy a React App on a Debian Server with Apache or Nginx",
        "type": "tech",
        "date": "Janvier 2025",
        "summary": "A step-by-step deployment guide for serving a production-ready React application on a Debian Linux server using either Apache or Nginx web server.",
        "fullContent": "Deploying a React application on your own Debian server gives you full control over performance, SSL, and traffic routing. This guide explains how to build and serve your React app using either Apache or Nginx, two of the most popular web servers in the Linux ecosystem.\n\n---\n\n### 🧰 Prerequisites\n\nBefore starting, make sure you have:\n\n- A Debian-based server (e.g., Debian 11 or Ubuntu 22.04)\n- Node.js and npm installed\n- Apache or Nginx installed\n- Root (sudo) access\n- Your React app ready (or cloned from Git)\n\n---\n\n### 🏗️ Step 1: Build the React Application\n\nFirst, generate a production build of your React app.\n\n'''bash\nnpm install\nnpm run build\n'''\n\nThis creates a 'build/' directory containing static files optimized for production.\n\n---\n\n### 🚀 Step 2: Copy the Build to the Server\n\nIf you're developing on your local machine:\n\n'''bash\nscp -r build/ user@your-server:/var/www/html/yourapp\n'''\n\nOr move it directly inside your Debian server:\n\n'''bash\nmv ~/your-react-project/build /var/www/html/yourapp\n'''\n\n---\n\n## Option 1: 🔧 Serve with Apache\n\n### 1. Enable Apache and Install Modules\n\n'''bash\nsudo apt update\nsudo apt install apache2\nsudo a2enmod rewrite\nsudo systemctl restart apache2\n'''\n\n### 2. Create a Virtual Host File\n\n'''bash\nsudo nano /etc/apache2/sites-available/yourapp.conf\n'''\n\nAdd:\n\n'''apache\n<VirtualHost *:80>\n    ServerName yourdomain.com\n    DocumentRoot /var/www/html/yourapp\n\n    <Directory /var/www/html/yourapp>\n        Options Indexes FollowSymLinks\n        AllowOverride All\n        Require all granted\n    </Directory>\n\n    ErrorLog ${APACHE_LOG_DIR}/yourapp-error.log\n    CustomLog ${APACHE_LOG_DIR}/yourapp-access.log combined\n</VirtualHost>\n'''\n\nEnable the config:\n\n'''bash\nsudo a2ensite yourapp\nsudo systemctl reload apache2\n'''\n\n### 3. Enable React Routing (Rewrite)\n\nCreate '.htaccess' in '/var/www/html/yourapp':\n\n'''bash\nnano /var/www/html/yourapp/.htaccess\n'''\n\nAdd:\n\n'''apache\nOptions -MultiViews\nRewriteEngine On\nRewriteCond %{REQUEST_FILENAME} !-f\nRewriteRule ^ index.html [QSA,L]\n'''\n\n---\n\n## Option 2: ⚙️ Serve with Nginx\n\n### 1. Install Nginx\n\n'''bash\nsudo apt update\nsudo apt install nginx\n'''\n\n### 2. Create a Server Block\n\n'''bash\nsudo nano /etc/nginx/sites-available/yourapp\n'''\n\nAdd:\n\n'''nginx\nserver {\n    listen 80;\n    server_name yourdomain.com;\n\n    root /var/www/html/yourapp;\n    index index.html;\n\n    location / {\n        try_files $uri /index.html;\n    }\n\n    access_log /var/log/nginx/yourapp-access.log;\n    error_log /var/log/nginx/yourapp-error.log;\n}\n'''\n\nEnable and reload:\n\n'''bash\nsudo ln -s /etc/nginx/sites-available/yourapp /etc/nginx/sites-enabled/\nsudo nginx -t\nsudo systemctl reload nginx\n'''\n\n---\n\n### 🔐 Optional: Enable HTTPS with Certbot\n\nIf your domain is pointed to the server, you can install an SSL certificate for free:\n\n'''bash\nsudo apt install certbot python3-certbot-nginx\nsudo certbot --nginx\n'''\n\nOr for Apache:\n\n'''bash\nsudo apt install certbot python3-certbot-apache\nsudo certbot --apache\n'''\n\n---\n\n### ✅ Final Testing\n\nVisit:\n\n'''\nhttp://yourdomain.com\n'''\n\nYour React app should be live!\n\n---\n\n### 🧩 Troubleshooting\n\n- Make sure ports 80 and 443 are open in your firewall (ufw or iptables).\n- Check file permissions: React files must be readable by 'www-data'.\n- Use browser dev tools to verify asset paths if you're seeing blank pages.\n\n---\n\n### 🎯 Conclusion\n\nWhether you choose Apache or Nginx, deploying a React app on a Debian server is straightforward and powerful. It gives you full control over routing, caching, and SSL setup. With a solid setup in place, your frontend can scale and run securely in production."
      },

      {
        "id": "Organize_Code",
        "title": "How I Organize My Code and Git Workflow for Freelance and Teams",
        "type": "tech",
        "date": "Janvier 2025",
        "summary": "A deep dive into my approach for structuring codebases, managing branches, and collaborating effectively in freelance and team environments using Git, CI/CD, and practical conventions.",
        "fullContent": `
      In both freelance and team settings, organizing your codebase and maintaining a clean Git workflow is critical for collaboration, maintainability, and long-term scalability. This blog post shares my comprehensive strategy for managing projects , from folder architecture to Git hygiene, branching strategies, commit messaging, and integrating CI/CD , based on years of real-world experience.
      
      ---
      
      ### 📁 1. Codebase Structure: Consistency Over Complexity
      
      Every project starts with structure. A predictable and consistent file/folder layout makes onboarding easy and debugging straightforward.
      
      #### For Frontend Projects (React):
      
      \'\'\'
      my-app/
      ├── public/
      │   └── index.html
      ├── src/
      │   ├── assets/
      │   ├── components/
      │   ├── pages/
      │   ├── services/
      │   ├── hooks/
      │   ├── utils/
      │   ├── context/
      │   ├── App.js
      │   └── index.js
      ├── .env
      ├── package.json
      └── README.md
      \'\'\'
      
      #### For Backend Projects (Spring Boot or Node.js):
      
      \'\'\'
      my-server/
      ├── src/
      │   ├── config/
      │   ├── controllers/
      │   ├── models/
      │   ├── routes/
      │   ├── services/
      │   └── utils/
      ├── test/
      ├── .env
      ├── .gitignore
      ├── package.json / pom.xml
      └── README.md
      \'\'\'
      
      > 📌 Tip: I use a README boilerplate template with instructions, dependencies, and API usage notes, always version-controlled.
      
      ---
      
      ### 🌱 2. Git Branching Strategy: Trunk-Based + Feature Branches
      
      For both solo and team-based projects, I follow a simplified version of Git Flow. Here's my branch hierarchy:
      
      - \'main\' – stable, production-ready code
      - \'dev\' – ongoing development base
      - \'feature/xxx\' – short-lived feature branches
      - \'bugfix/xxx\' – for specific hotfixes
      - \'release/xxx\' – for final pre-release polishing
      
      #### Workflow Summary:
      
      1. Clone the repository:
         \'git clone https://github.com/your/repo.git\'
      2. Create feature branch:
         \'git checkout -b feature/add-user-login\'
      3. Regular commits and pushes:
         \'git commit -m "feat: implement login screen"\'
      4. Merge into \'dev\' via Pull Request (PR) or manually
      5. Periodically merge \'dev\' → \'main\' for releases
      
      ---
      
      ### ✍️ 3. Commit Message Conventions
      
      I use [Conventional Commits](https://www.conventionalcommits.org/) to maintain semantic clarity.
      
      \'\'\'
      feat: add user profile page
      fix: correct overflow bug on dashboard
      docs: update README for setup
      style: reformat code with Prettier
      refactor: improve API response handler
      \'\'\'
      
      > Why? It improves readability and works great with automated changelog generators and CI tools.
      
      ---
      
      ### 🔁 4. PR Review Etiquette and Standards
      
      In team projects, I enforce lightweight but useful code review rituals:
      
      - Every PR must be under ~400 lines (smaller = easier to review)
      - Must have a descriptive title and a summary of changes
      - Linked to a task/ticket or explained with context
      - Screenshots or testing evidence where applicable
      - Avoid “WIP” PRs on main repos (keep them in forks or branches)
      
      ---
      
      ### 🔧 5. CI/CD Integration (GitHub Actions or GitLab CI)
      
      I configure pipelines as early as possible.
      
      #### For React apps:
      
      - Lint → Test → Build → Deploy
      - Hosting: Vercel, Netlify, or rsync to a Linux VPS
      
      #### For Spring Boot apps:
      
      - Maven clean + test
      - Build JAR and push to a remote server via SSH + systemctl restart
      
      Sample GitHub Actions workflow:
      
      \'\'\'yaml
      name: CI
      
      on: [push]
      
      jobs:
        build:
          runs-on: ubuntu-latest
          steps:
            - uses: actions/checkout@v3
            - name: Install deps
              run: npm ci
            - name: Run tests
              run: npm test
            - name: Build
              run: npm run build
      \'\'\'
      
      ---
      
      ### 📘 6. Git Hygiene Tips for Every Project
      
      - Never commit directly to \'main\'
      - Avoid \'git push -f\' unless coordinated
      - Rebase frequently: \'git pull --rebase\'
      - Squash commits before merging: \'git rebase -i\'
      - Tag releases: \'git tag -a v1.0.0 -m "first stable"\'
      
      ---
      
      ### 🤝 7. Freelance vs Team Differences
      
      | Aspect             | Freelance Projects                      | Team Collaboration                     |
      |--------------------|-----------------------------------------|----------------------------------------|
      | Branch strategy    | Simple (main + feature)                 | Full Git Flow or Trunk-based           |
      | Code reviews       | Optional, sometimes async via client    | Mandatory peer review                  |
      | Documentation      | Minimal + client-specific               | Standardized internal + onboarding     |
      | Communication      | Email / Trello / WhatsApp               | Slack + GitHub Issues + Sprint boards  |
      | Deployment         | FTP / rsync / shared hosting            | CI/CD pipelines + staging/prod envs    |
      
      ---
      
      ### 📚 8. Tools I Use for Git and Code Management
      
      - **VS Code + GitLens** for visual Git navigation
      - **SourceTree** or **GitKraken** for large repos
      - **Prettier + ESLint** for formatting consistency
      - **husky + lint-staged** for pre-commit hooks
      - **.editorconfig** for shared indentation rules
      - **README + CHANGELOG + LICENSE** always included
      
      ---
      
      ### 🔄 9. Handling Conflicts and Rollbacks
      
      - Keep PRs small = fewer conflicts
      - Use \'git stash\' to save local work before switching
      - Use \'git reflog\' to recover from mistakes
      - For critical rollbacks: create a patch branch from previous commit
        \'git checkout -b hotfix/rollback-issue HEAD~1\'
      
      ---
      
      ### 🧠 Final Thoughts
      
      Clean Git workflows are more than a habit , they are a productivity multiplier. Whether you're a freelancer pushing client deliverables or a developer collaborating in large teams, the ability to clearly structure code and collaborate seamlessly pays off at every stage of the project.
      
      By aligning on structure, communication, and tooling, you create a shared language for success.
      
      > “Clean code, clean commits, clean launches.”
      
      Happy coding!
        `
      },
      
      {
        "id": "Tools",
        "title": "The Tools I Use Daily as a Full-Stack Developer in 2025",
        "type": "tech",
        "date": "Fevrier 2025",
        "summary": "A comprehensive walkthrough of the essential tools, platforms, and environments I rely on daily as a modern full-stack developer, from coding and debugging to project management and deployment.",
        "fullContent": "In the fast-evolving world of software development, having the right tools makes the difference between frustration and flow. As a full-stack developer working across mobile, backend, frontend, DevOps, and freelance projects, I've fine-tuned my toolkit to stay fast, productive, and focused. Here's a full overview of what I use daily in 2025 , and why.\n\n---\n\n### 👨‍💻 Code Editors & IDEs\n\n**🟣 VS Code**\n- My default editor for frontend (React, Flutter), backend (Node.js), and scripting.\n- Extensions:\n  - Prettier\n  - ESLint\n  - GitLens\n  - REST Client\n  - Tailwind IntelliSense\n\n**🟡 IntelliJ IDEA Ultimate**\n- For heavy Spring Boot backend projects.\n- Best-in-class for Java autocompletion, refactoring, and Maven/Gradle support.\n\n---\n\n### 🏗️ Tech Stack\n\n**Frontend:**\n- React.js with Vite or CRA\n- Flutter for cross-platform mobile + web\n- TailwindCSS + shadcn/ui\n\n**Backend:**\n- Spring Boot (REST APIs, Postgres, authentication)\n- Node.js (Lightweight APIs, CLI tools)\n- Express + Prisma for quick prototypes\n\n**Database:**\n- PostgreSQL (main relational DB)\n- Redis (for caching, sessions)\n- Firebase (auth & storage for mobile apps)\n\n**Geospatial:**\n- PostGIS\n- OpenLayers\n- GeoServer\n\n---\n\n### 🧪 Testing & Debugging\n\n**Jest + React Testing Library**\n- Unit & integration tests for frontend\n\n**Postman & Insomnia**\n- For REST API testing\n\n**Spring Boot DevTools**\n- Hot reload + actuator endpoints\n\n**Chrome DevTools + Redux DevTools**\n- UI/UX debugging essentials\n\n---\n\n### 🌐 Git & Version Control\n\n**GitHub**\n- Pull requests, actions, issue tracking\n\n**GitKraken**\n- Visual merge conflict resolution\n\n**Git CLI**\n- Daily workflow:\n  '''bash\n  git checkout -b feature/blog-api\n  git commit -m \"feat: add blog create endpoint\"\n  git pull --rebase\n  git push origin feature/blog-api\n  '''\n\n---\n\n### 🐳 DevOps & Infrastructure\n\n**Debian VPS + Apache/Nginx**\n- Self-managed hosting for apps and dashboards\n\n**Docker**\n- Local development and reproducible environments\n\n**GitHub Actions**\n- CI/CD pipelines for building, testing, and deploying\n\n**Certbot + Let's Encrypt**\n- HTTPS SSL setup in seconds\n\n---\n\n### ☁️ Cloud Platforms\n\n**DigitalOcean**\n- Affordable VPS hosting\n\n**Firebase**\n- Auth + cloud storage + Realtime DB for Flutter apps\n\n**Cloudflare**\n- DNS + security + caching\n\n**Vercel & Netlify**\n- For deploying frontend projects instantly\n\n---\n\n### 🧩 Project Management & Productivity\n\n**Notion**\n- Personal dashboard, blog drafts, tech specs\n\n**Trello / GitHub Projects**\n- Task management & client deliverables\n\n**ChatGPT & Copilot**\n- Coding assistant, regex generation, doc summarization\n\n**Loom & OBS Studio**\n- Screen recording for code demos and client tutorials\n\n---\n\n### 🎨 Design & Assets\n\n**Figma**\n- UI/UX prototyping + Flutter mockups\n\n**Canva Pro**\n- Quick social posts, banners, pitch decks\n\n**Heroicons + Feather Icons**\n- Used across all apps and dashboards\n\n---\n\n### 🧠 Developer Utilities\n\n**htop, ncdu, netstat**\n- For server diagnostics\n\n**nvm + pyenv**\n- Managing Node and Python versions\n\n**tmux + oh-my-zsh**\n- Terminal productivity boost\n\n**Glances**\n- Real-time Linux server monitoring\n\n---\n\n### 🛡️ Security & Backup\n\n**UFW + fail2ban**\n- Basic firewall + brute-force protection\n\n**rclone + rsync + cron**\n- Scheduled backups to external disks / cloud\n\n**Bitwarden**\n- Secure password management\n\n---\n\n### 🎯 Conclusion\n\nIn 2025, full-stack development goes beyond just code , it’s about managing infrastructure, working with clients, staying productive, and shipping reliable software. By investing in the right tools and continuously refining your workflow, you can build faster, smarter, and with confidence.\n\nThe tools might evolve, but the mindset remains: **build clean, test early, automate everything, and document well**.\n\n> “Don’t let your tools limit you , let them empower your creativity.”"
      },

      {
        "id": "Launch Web Apps",
        "title": "From Localhost to Live: How I Launch Web Apps on My Own VPS",
        "type": "tech",
        "date": "Fevrier 2025",
        "summary": "A full step-by-step walkthrough of how I deploy my web apps from development mode on localhost to live production using a self-managed Debian VPS with Apache, Nginx, and secure remote access.",
        "fullContent": "Deploying your own web applications without relying on third-party platforms gives you ultimate control over performance, security, and costs. Over the years, I’ve built a simple but powerful routine to move apps from local development to production on my Debian VPS. In this blog, I’ll guide you step-by-step , no Docker required, just solid fundamentals.\n\n---\n\n### 🧰 My Environment Setup\n\n- **Local Dev OS:** Windows with WSL2 / Ubuntu or Linux directly\n- **VPS OS:** Debian 11 or 12 (Minimal install)\n- **Web Stack:** React + Spring Boot / Node.js + PostgreSQL\n- **Web Server:** Apache or Nginx\n- **Database:** Local PostgreSQL, later mirrored remotely\n\n---\n\n### 1️⃣ Step 1: Prepare the Production Server (One-Time Setup)\n\n✅ SSH into your VPS:\n'''bash\nssh root@your-ip-address\n'''\n\n✅ Update and install tools:\n'''bash\napt update && apt upgrade -y\napt install sudo ufw curl unzip zip git nano -y\n'''\n\n✅ Create a non-root user:\n'''bash\nadduser myuser\nusermod -aG sudo myuser\n'''\n\n✅ Setup a firewall:\n'''bash\nufw allow OpenSSH\nufw allow 80\nufw allow 443\nufw enable\n'''\n\n✅ Enable key-based SSH login (recommended):\n'''bash\nssh-copy-id myuser@your-ip\n'''\n\n✅ Install Apache or Nginx:\n'''bash\napt install apache2\n# or\napt install nginx\n'''\n\n✅ Install certbot for SSL later:\n'''bash\napt install certbot python3-certbot-nginx  # or ...apache\n'''\n\n---\n\n### 2️⃣ Step 2: Build Your App Locally\n\nFor React:\n'''bash\nnpm run build\n'''\nFor Spring Boot:\n'''bash\n./mvnw clean package\n'''\nFor Node.js:\n'''bash\nnpm run build  # or nothing if pure API\n'''\n\nYou now have a '/build' or '/dist' or '/target/app.jar' ready to ship.\n\n---\n\n### 3️⃣ Step 3: Transfer Files to the Server\n\nUse 'scp' or 'rsync' from local to server:\n\n'''bash\nscp -r build/ myuser@your-ip:/var/www/html/yourapp\n'''\n\nFor Spring Boot:\n'''bash\nscp target/myapp.jar myuser@your-ip:/opt/myapp/\n'''\n\nFor Node.js:\n'''bash\nscp -r . myuser@your-ip:/opt/my-node-app\n'''\n\n---\n\n### 4️⃣ Step 4: Configure Apache or Nginx\n\n**For React (Apache):**\n'''apache\n<VirtualHost *:80>\n  ServerName yourdomain.com\n  DocumentRoot /var/www/html/yourapp\n\n  <Directory /var/www/html/yourapp>\n    Options -Indexes +FollowSymLinks\n    AllowOverride All\n    Require all granted\n  </Directory>\n</VirtualHost>\n'''\nEnable with:\n'''bash\na2ensite yourapp.conf\nsystemctl reload apache2\n'''\n\n**For React (Nginx):**\n'''nginx\nserver {\n  listen 80;\n  server_name yourdomain.com;\n  root /var/www/html/yourapp;\n  index index.html;\n\n  location / {\n    try_files $uri /index.html;\n  }\n}\n'''\nEnable with:\n'''bash\nln -s /etc/nginx/sites-available/yourapp /etc/nginx/sites-enabled/\nsystemctl reload nginx\n'''\n\n---\n\n### 5️⃣ Step 5: Setup Spring Boot or Node Backend as a Service\n\nCreate a systemd service for Spring Boot:\n'''ini\n[Unit]\nDescription=Spring Boot App\nAfter=network.target\n\n[Service]\nUser=myuser\nExecStart=/usr/bin/java -jar /opt/myapp/myapp.jar\nRestart=always\n\n[Install]\nWantedBy=multi-user.target\n'''\n\nSave as '/etc/systemd/system/myapp.service' and start it:\n'''bash\nsystemctl daemon-reexec\nsystemctl enable myapp\nsystemctl start myapp\n'''\n\nSame for Node.js:\n'''bash\nExecStart=/usr/bin/node /opt/my-node-app/index.js\n'''\n\nThen reverse proxy it from Nginx:\n'''nginx\nlocation /api {\n  proxy_pass http://localhost:3000;\n  proxy_http_version 1.1;\n  proxy_set_header Upgrade $http_upgrade;\n  proxy_set_header Connection 'upgrade';\n  proxy_set_header Host $host;\n  proxy_cache_bypass $http_upgrade;\n}\n'''\n\n---\n\n### 6️⃣ Step 6: Add SSL with Certbot (Free HTTPS)\n\n'''bash\nsudo certbot --nginx -d yourdomain.com\n# or\nsudo certbot --apache -d yourdomain.com\n'''\nDone! You now have HTTPS enabled in seconds.\n\n---\n\n### 🛠 Bonus: Quick Deployment Script with rsync\n\n'''bash\nrsync -avz ./build/ myuser@your-ip:/var/www/html/yourapp/\n'''\nOr automate build + deploy:\n'''bash\nnpm run build && scp -r build/ myuser@IP:/var/www/html/\n'''\n\n---\n\n### ✅ Conclusion\n\nDeploying to your own VPS gives you full flexibility, cost savings, and better learning. It’s not complicated , just structured. Once you set up the basic pipeline, updating and managing your apps becomes a smooth and repeatable task.\n\n> From localhost to live , in 6 reliable steps.\n\nIn the next blogs, I’ll show how to automate this with GitHub Actions and make updates even faster 🚀."
      },

      {
        "id": "Multi-Language_React_Apps",
        "title": "Building Multi-Language React Apps: i18n Without the Pain",
        "type": "tech",
        "date": "2025",
        "summary": "An in-depth guide to implementing internationalization (i18n) in React apps using best practices, smart tooling, and scalable strategies , without breaking your component architecture or workflow.",
        "fullContent": "In 2025, having a multilingual React application isn't a luxury , it's a necessity. Whether you're building a SaaS dashboard, an e-commerce store, or a government platform, supporting multiple languages is key to accessibility and market growth. Fortunately, modern tools like 'react-i18next' make internationalization (i18n) smooth and powerful , when done right.\n\nIn this guide, I’ll share how I structure and scale multilingual React apps without pain or performance problems.\n\n---\n\n### 🌍 Why i18n Matters\n\n- **Global Audience:** Reach more users by speaking their language.\n- **Compliance:** For public sector or EU apps, localization is a legal requirement.\n- **Better UX:** Users engage more when content feels native.\n\n---\n\n### 🧰 What I Use\n\n- **react-i18next** (based on i18next)\n- **i18next-browser-languagedetector**\n- **i18next-http-backend** (optional for remote loading)\n\nInstall:\n'''bash\nnpm install i18next react-i18next i18next-browser-languagedetector\n'''\n\n---\n\n### 🗂️ Folder Structure\n\n'''bash\nsrc/\n├── i18n/\n│   ├── index.js\n│   └── locales/\n│       ├── en/translation.json\n│       └── fr/translation.json\n'''\n\n---\n\n### ⚙️ i18n Initialization (src/i18n/index.js)\n\n'''js\nimport i18n from 'i18next';\nimport { initReactI18next } from 'react-i18next';\nimport LanguageDetector from 'i18next-browser-languagedetector';\n\nimport en from './locales/en/translation.json';\nimport fr from './locales/fr/translation.json';\n\ni18n\n  .use(LanguageDetector)\n  .use(initReactI18next)\n  .init({\n    resources: {\n      en: { translation: en },\n      fr: { translation: fr }\n    },\n    fallbackLng: 'en',\n    interpolation: {\n      escapeValue: false\n    }\n  });\n\nexport default i18n;\n'''\n\nThen import 'i18n.js' in your 'App.js':\n'''js\nimport './i18n';\n'''\n\n---\n\n### 📄 Translation JSON Example\n\n'''json\n{\n  \"welcome\": \"Welcome to our app!\",\n  \"login\": \"Login\",\n  \"logout\": \"Logout\"\n}\n'''\n\nFrench version:\n'''json\n{\n  \"welcome\": \"Bienvenue sur notre application !\",\n  \"login\": \"Connexion\",\n  \"logout\": \"Déconnexion\"\n}\n'''\n\n---\n\n### 🧠 Using Translations in Components\n\n'''js\nimport { useTranslation } from 'react-i18next';\n\nfunction Header() {\n  const { t, i18n } = useTranslation();\n\n  return (\n    <header>\n      <h1>{t('welcome')}</h1>\n      <button onClick={() => i18n.changeLanguage('fr')}>FR</button>\n      <button onClick={() => i18n.changeLanguage('en')}>EN</button>\n    </header>\n  );\n}\n'''\n\n---\n\n### 🧩 Advanced Patterns\n\n- **Interpolation:**\n'''json\n\"greeting\": \"Hello, {{name}}!\"\n'''\n'''js\nt('greeting', { name: 'Raed' })\n'''\n\n- **Pluralization:**\n'''json\n\"car\": \"{{count}} car\",\n\"car_plural\": \"{{count}} cars\"\n'''\n'''js\nt('car', { count: 1 })\nt('car', { count: 5 })\n'''\n\n- **Nesting:**\n'''json\n\"title\": \"{{appName}} - Dashboard\"\n'''\n'''js\nt('title', { appName: t('projectName') })\n'''\n\n---\n\n### 🚀 Performance Tips\n\n- Use code splitting for translation files if you support 10+ languages\n- Avoid calling 't()' inside loops or unnecessary renders\n- Memoize translated content in large UIs\n\n---\n\n### 📦 Optional: Dynamic Backend Loading\n\nIf you want to fetch translations via HTTP (e.g., from a CMS or a CDN):\n'''bash\nnpm install i18next-http-backend\n'''\nThen add:\n'''js\nimport Backend from 'i18next-http-backend';\n...\n.use(Backend)\n'''\n\nConfigure URLs to fetch:\n'''js\nbackend: {\n  loadPath: '/locales/{{lng}}/translation.json'\n}\n'''\n\n---\n\n### 🧪 Testing i18n\n\nUse 'react-i18next'’s 'I18nextProvider' to wrap components during tests. Also use mock language files to ensure coverage.\n\n---\n\n### ✅ Conclusion\n\nReact i18n can be painless , if you structure it well and use the right conventions. 'react-i18next' is incredibly powerful, supporting nested keys, plurals, fallbacks, and even language detection. \n\nWith just a little effort upfront, your app becomes accessible to users worldwide.\n\n> “Localization is not just translation , it's user respect.”"
      },

      {
        "id": "Mastering_Scheduled_Tasks",
        "title": "Mastering Scheduled Tasks in Linux: Cron Jobs for Devs",
        "type": "tech",
        "date": " 2025",
        "summary": "A practical and powerful guide to using cron jobs to automate backups, updates, monitoring, and reporting as a developer managing Linux-based servers and projects.",
        "fullContent": "Automation is a superpower , and on Linux, 'cron' is your wand. Whether you're running backup scripts, sending uptime reports, or auto-deploying builds, cron jobs let you execute tasks automatically at precise intervals. In this guide, I’ll share how I use cron daily as a developer to automate my work and server operations reliably.\n\n---\n\n### 🧠 What Is a Cron Job?\n\nA **cron job** is a scheduled task that runs in the background at specific times or intervals, defined in a **crontab** file.\n\n---\n\n### 🔧 Basic Cron Syntax\n\nA cron schedule consists of 5 fields:\n'''bash\n* * * * * /path/to/command.sh\n| | | | |\n| | | | └── Day of week (0-7) (Sun=0 or 7)\n| | | └──── Month (1-12)\n| | └────── Day of month (1-31)\n| └──────── Hour (0-23)\n└────────── Minute (0-59)\n'''\n\n**Example: Run every day at 2:30 AM**\n'''bash\n30 2 * * * /home/raed/scripts/backup.sh\n'''\n\n---\n\n### 📁 Managing Your Crontab\n\nTo edit your personal crontab:\n'''bash\ncrontab -e\n'''\nTo view scheduled jobs:\n'''bash\ncrontab -l\n'''\nTo remove:\n'''bash\ncrontab -r\n'''\n\n---\n\n### 🧪 Test-Friendly Logging\n\nAlways log your cron output:\n'''bash\n0 * * * * /home/raed/scripts/db-backup.sh >> /var/log/db-backup.log 2>&1\n'''\n\n---\n\n### 🧰 Real-World Use Cases\n\n#### 1️⃣ Daily Backup to External Drive\n'''bash\n0 3 * * * rsync -av --delete /var/www/ /mnt/backup/www/ >> /var/log/www-backup.log 2>&1\n'''\n\n#### 2️⃣ Ping Uptime Monitor\n'''bash\n*/5 * * * * ping -c 1 google.com || echo \"Internet is down at $(date)\" >> /var/log/network.log\n'''\n\n#### 3️⃣ Weekly Git Auto-Push\n'''bash\n0 12 * * 1 cd /home/raed/projects/myapp && git add . && git commit -m \"[auto] weekly sync\" && git push\n'''\n\n#### 4️⃣ Cleanup Logs\n'''bash\n0 0 * * 0 find /var/log/myapp/ -type f -mtime +7 -delete\n'''\n\n---\n\n### 🔄 Environment Variables\n\nCrontab does not load your full shell. Always use full paths:\n'''bash\n/path/to/node /home/user/app/script.js\n'''\n\nCheck your env by adding:\n'''bash\nenv > /tmp/env.txt\n'''\n\n---\n\n### 🔔 Email Alerts for Failures\n\nYou can configure cron to send email when a job fails:\n'''bash\nMAILTO=\"your@email.com\"\n'''\nThis must be at the top of your crontab. Use 'postfix' or 'msmtp' for mail delivery.\n\n---\n\n### 🔍 Debugging Cron Jobs\n\nCommon issues:\n- Paths are incorrect , use full paths (e.g., '/usr/bin/python3')\n- No output , redirect stdout and stderr\n- Permissions , script isn’t executable\n\nTips:\n- Add '#!/bin/bash -x' at the top of your script\n- Log outputs to debug files: '/tmp/mycron.log'\n\n---\n\n### 🛡️ Security and Best Practices\n\n- Use 'crontab -u username -e' for managing others' jobs\n- Limit access: cron is enabled per user by default\n- Validate scripts work manually before adding to cron\n- Use 'anacron' for jobs that must run even if the system was off\n\n---\n\n### 🧠 Bonus: crontab.guru Cheatsheet\n\nUse https://crontab.guru to generate or understand expressions quickly.\n\n---\n\n### ✅ Conclusion\n\nCron is one of the most underused but powerful tools in a developer's toolbox. It’s minimal, doesn’t require heavy frameworks, and works on every Linux distro. Once you start automating repetitive tasks with cron, you’ll wonder how you lived without it.\n\n> \"Automate the boring stuff. Focus on building.\""
      },
      
      {
        "id": "Debugging_Toolbox",
        "title": "When Things Break: My Debugging Toolbox for Java, Node, and React",
        "type": "tech",
        "date": "2025",
        "summary": "A practical, real-world guide to how I troubleshoot bugs and unexpected behavior in Java (Spring Boot), Node.js, and React , including tools I rely on, personal techniques, and the mindset I use when production crashes or something just stops working.",
        "fullContent": `Debugging is not just a technical step , it's a mental process. Whether I’m building a Java backend, working on a Node.js API, or fixing layout bugs in React, debugging is a daily reality. Over time, I've built a personal system and toolbox that helps me stay calm, find problems fast, and fix them properly.
      
      This post shares the tools, strategies, and small lessons that help me when things break.
      
      ---
      
      ### 🧠 My Debugging Philosophy
      
      - **Never assume. Always verify.**
      - **Reproduce first.** You can’t fix what you can’t reproduce.
      - **Divide and conquer.** Narrow the scope.
      - **Log everything. Then inspect and question.**
      
      ---
      
      ### 🧪 Debugging React Frontend
      
      #### 🔧 Tools I Use
      - Chrome DevTools
      - React Developer Tools
      - Redux DevTools
      - VS Code debugger
      
      #### 🛠 What I Check
      1. **Console Errors**
         - Don’t ignore red logs. They’re your guide.
      2. **Props & State**
         - Use React DevTools to inspect what's being passed.
      3. **Network Tab**
         - Are API calls firing? Are they failing? Is it a 404 or 500? CORS issue?
      4. **Render Failures**
         - Sometimes it’s a wrong key, or a broken '.map()'
      5. **Empty UI**
         - Log your props:  
           \'\'\'js
           console.log('Props:', props);
           \'\'\'
      6. **CSS/DOM Inspection**
         - Use the Elements tab to debug layout and hidden elements.
      
      ---
      
      ### 🐢 Debugging Node.js Backend
      
      #### 🔧 Tools
      - 'console.log()' (don’t judge , it still works)
      - 'debug' npm module
      - VS Code debugger + launch.json
      - Postman/Insomnia
      
      #### 🛠 Checklist
      1. **Start small** , isolate the endpoint or logic
      2. **Async issues?**
         \'\'\'js
         await something().catch(console.error);
         \'\'\'
      3. **Silent crashes**
         - Use:
           \'\'\'js
           process.on('uncaughtException', console.error);
           process.on('unhandledRejection', console.error);
           \'\'\'
      4. **Environment mismatch**
         - Check '.env', ports, configs.
      5. **Manual API testing**
         - Postman, curl, Insomnia , never guess.
      
      ---
      
      ### ☕ Debugging Java (Spring Boot)
      
      #### 🔧 Tools
      - IntelliJ debugger (breakpoints, watches)
      - Spring Boot devtools
      - '@Slf4j' and loggers
      - Postman
      - '/actuator' endpoints
      
      #### 🛠 Checklist
      1. **Rebuild cleanly**
         - 'mvn clean install'
      2. **Log in detail**
         \'\'\'java
         log.info("User ID: {}, Status: {}", userId, status);
         \'\'\'
      3. **Check configurations**
         - 'application.properties' or 'application.yml'
      4. **Enable SQL Logging**
         \'\'\'properties
         spring.jpa.show-sql=true
         spring.jpa.properties.hibernate.format_sql=true
         \'\'\'
      5. **Breakpoints + watches**
         - Don't guess values , inspect them live.
      
      ---
      
      ### 🧩 Common Issues I See
      
      | Problem                           | Solution Tip                                  |
      |----------------------------------|-----------------------------------------------|
      | 'undefined is not a function'   | Check async timing, null checks               |
      | React component not re-rendering | Ensure proper state update or useEffect deps  |
      | CORS errors                      | Check backend headers, browser config         |
      | Java 500 error                   | Check backend logs, DTO mismatches            |
      | Node API returns undefined       | Trace async flow, missing 'await'?            |
      
      ---
      
      ### 📦 My Favorite Debug Snippets
      
      #### React / Node.js
      \'\'\'js
      // Pretty print nested object
      console.log(JSON.stringify(myObj, null, 2));
      \'\'\'
      
      #### Java
      \'\'\'java
      // Print all loaded beans in Spring
      System.out.println(Arrays.toString(ctx.getBeanDefinitionNames()));
      \'\'\'
      
      #### Bash
      \'\'\'bash
      # Monitor logs in real-time
      journalctl -u myapp.service -f
      
      # Find what’s running on port 8080
      lsof -i :8080
      \'\'\'
      
      ---
      
      ### ✅ Final Thoughts
      
      Debugging isn't a talent , it's a process. It's about being calm, methodical, and curious. Some days you'll fix a bug in 2 minutes. Other days you'll chase a config error for hours. But every time, you’ll learn something new.
      
      > “The best developers aren't those who write code fast , they're the ones who fix it well.”
      
      `,
        "keywords": "debugging React, fix Java backend bug, Node.js error trace, frontend troubleshooting guide, Chrome DevTools, React Developer Tools, Redux DevTools, VS Code debugger, Postman Java API, Spring Boot logs, fix CORS error, Node uncaughtException, async await bug fix, Java logback slf4j, backend API null fix, actuator health check, Node console.log debugging, curl API test, react empty component, Java HTTP 500 debug"
      }
      ,

      {
        "id": "Securing_Backend_APIs",
        "title": "Securing Your Backend APIs: Auth, Rate-Limiting, and Headers",
        "type": "tech",
        "date": "2025",
        "summary": "A hands-on, developer-first guide to securing backend APIs in 2025,covering authentication strategies, rate-limiting techniques, and essential HTTP headers to protect your services.",
        "fullContent": "APIs are the backbone of modern applications, but they are also prime targets for attacks. As a full-stack developer managing backend services, I've learned that robust API security isn't optional,it's essential. Here's how I approach securing APIs in 2025.\n\n---\n\n### 🔐 Authentication: Verifying Who's Calling\n\n**1. API Keys**\n- **Use case:** Simple projects or internal services.\n- **Implementation:** Generate unique keys for each client and validate them on each request.\n- **Tip:** Store keys securely and rotate them periodically.\n\n**2. OAuth 2.0**\n- **Use case:** Public APIs or third-party integrations.\n- **Implementation:** Utilize flows like Authorization Code or Client Credentials.\n- **Tip:** Define scopes to limit access and use refresh tokens for long-lived sessions.\n\n**3. JWT (JSON Web Tokens)**\n- **Use case:** Stateless authentication across services.\n- **Implementation:** Encode user information and permissions in the token.\n- **Tip:** Set appropriate expiration times and validate signatures.\n\n---\n\n### 🚦 Rate Limiting: Controlling Traffic\n\n**1. Fixed Window**\n- **Description:** Allows a set number of requests per fixed time window.\n- **Use case:** Simple rate limiting needs.\n\n**2. Sliding Window**\n- **Description:** Provides a more granular control by tracking requests over a rolling time window.\n- **Use case:** APIs requiring smoother traffic distribution.\n\n**3. Token Bucket**\n- **Description:** Allows bursts of traffic while enforcing a steady rate over time.\n- **Use case:** APIs with variable traffic patterns.\n\n**4. Implementation Tips:**\n- **Per-User Limits:** Apply limits based on API keys or user identifiers.\n- **Per-IP Limits:** Useful for public APIs to prevent abuse.\n- **Response Headers:** Inform clients of their rate limit status using headers like 'X-RateLimit-Limit', 'X-RateLimit-Remaining', and 'X-RateLimit-Reset'.\n\n---\n\n### 🛡️ HTTP Security Headers: Strengthening Defenses\n\n**1. Content Security Policy (CSP)**\n- **Purpose:** Prevents Cross-Site Scripting (XSS) attacks.\n- **Implementation:** Define allowed sources of content.\n\n**2. Strict-Transport-Security (HSTS)**\n- **Purpose:** Enforces secure (HTTPS) connections.\n- **Implementation:** Add the 'Strict-Transport-Security' header with appropriate max-age.\n\n**3. X-Frame-Options**\n- **Purpose:** Prevents clickjacking by controlling whether the site can be framed.\n- **Implementation:** Set to 'DENY' or 'SAMEORIGIN'.\n\n**4. X-Content-Type-Options**\n- **Purpose:** Prevents MIME type sniffing.\n- **Implementation:** Set to 'nosniff'.\n\n**5. Referrer-Policy**\n- **Purpose:** Controls the amount of referrer information sent.\n- **Implementation:** Choose a policy like 'no-referrer' or 'strict-origin'.\n\n---\n\n### 🧰 Additional Security Measures\n\n**1. Input Validation**\n- **Purpose:** Prevents injection attacks.\n- **Implementation:** Validate and sanitize all incoming data.\n\n**2. Logging and Monitoring**\n- **Purpose:** Detects and responds to suspicious activities.\n- **Implementation:** Log all requests and monitor for anomalies.\n\n**3. HTTPS Everywhere**\n- **Purpose:** Ensures data in transit is encrypted.\n- **Implementation:** Use TLS certificates and redirect HTTP to HTTPS.\n\n**4. CORS (Cross-Origin Resource Sharing)**\n- **Purpose:** Controls which origins can access your API.\n- **Implementation:** Set appropriate 'Access-Control-Allow-Origin' headers.\n\n---\n\n### ✅ Conclusion\n\nSecuring backend APIs is a continuous process that involves multiple layers of defense. By implementing strong authentication, effective rate limiting, and essential security headers, you can protect your APIs against common threats. Stay vigilant and regularly update your security practices to adapt to evolving challenges."
      },








      {
        "id": "Mining",
        "title": "Mining Ethereum as a Broke Student: How I Built My First Rig and Rode the Last Wave Before the Merge",
        "type": "personal",
        "date": "2025",
        "summary": "A personal journey through Ethereum GPU mining: from a single GTX 1650 to building a real mining rig with RX 580s and RTX 30 series cards. How it all started in my garage, what I learned, and how it led me into the world of trading.",
"fullContent": `Back in 2019, I was an *engineering student in Tunisia*.  
Not the kind of student with connections, money, or side hustles. Just a regular guy trying to survive exams, live on instant noodles, and find something, anything, that felt like a breakthrough.

I wasn't looking for crypto. I wasn’t chasing fast money.  
I was looking for an escape. A project. A small win to make me feel like I wasn’t stuck in a system where success only came through grades and diplomas.

It didn’t start with Reddit or some deep tech blog.  
It happened during a regular weekend , the kind I spent like every other, heading back home from university to see my parents.

I studied over 100 kilometers away, so every weekend felt like a small escape from the pressure of school. I'd reconnect with family, eat decent food for once, and meet up with old friends , the ones I grew up with.

One Saturday afternoon, we were sitting at a local café in my hometown , cheap coffee, loud street noise, and jokes flying across the table like usual. Nothing serious.  
But suddenly, one of my friends , the type who always dabbles in weird stuff , dropped something unexpected:

**“You know people are making money just by running a graphics card at home?”**

I blinked. “What, like mining? That crypto stuff?”  
He nodded. “Yeah. Ethereum. I know a guy who’s doing it. Just leaves his PC running all day, makes a few dollars daily without lifting a finger.”

Wait… What?  
You’re telling me I can earn crypto , real money , just by letting my PC run?

I had never even touched a wallet, never read a whitepaper, never thought about blockchain.  
At first, I laughed it off. But something about it stuck with me. Maybe it was the way he said it , not trying to brag, just sharing something strange that actually worked.

Later that night, back at my parents’ house, I couldn’t stop thinking about it. I opened my laptop and started searching.  
One video became five. One article turned into a full night of learning about wallets, hash rates, electricity, and GPU models.

I didn’t sleep that night. I didn’t understand half of it, but I didn’t stop either.

The next day, I opened my laptop and launched my first mining software.  
I had a **GTX 1650** , nothing special, but hey, it was a start.  
It ran. It hashed. The fans spun up like it meant business. And for a few moments, I felt like I was doing something futuristic.

But reality hit quick.  
After a couple of hours, the numbers on the screen were… modest at best.  
My earnings? Maybe $1 or $2 a day, if I was lucky.  
But when you subtracted the cost of electricity, the fan spinning all day, and the risk of blowing a fuse in the house , it felt like I was mining more heat than crypto.

Still, even with the low rewards, something in me didn’t want to stop.  
Because that $1 felt different.  
It felt like I made it , not from a job, not from someone else’s approval, but from my own curiosity and action.

My room? Hot. Loud. And the performance? Worse than watching paint dry on a frozen wall.  
That should’ve been the end. A small experiment. Close the laptop. Move on.

But somehow, I couldn't.  
Because for the first time, something felt possible.  
Not easy. Not rich. Just… possible.

---

### Tunisia, Heat & Fans: My First Mining Struggle

Many of you know that Tunisia isn’t exactly known for cool weather , it's hot nearly all year.  
So imagine trying to mine with zero airflow and a GPU screaming at 80°C.  
My room felt like a server farm inside a sauna.

I quickly added fans , literally. I bought a few cheap but powerful fans and built a Frankenstein cooling system:  
One fan to push air in, another to pull it out, and one more just blowing at me while I monitored the hash rates.  
It wasn’t pretty, but it worked. The temps dropped. The rig ran smoother. The room became bearable. My GPUs survived.

---

### A Brother's Bet on My Dreams

After realizing the 1650 wouldn't cut it, I started planning a real setup.  
I watched YouTube tutorials at 2x speed, joined Telegram groups, and read mining calculators like they were sacred texts.  
I wanted to go all in , but how?

I was waiting for my bourse, and as crazy as it sounds, I even tried talking to my parents about helping me out.  
But they didn’t believe it.

**“You’re telling me you’re going to make money by running a computer 24/24 with a graphics card? That’s going to burn electricity and your brain.”**

I couldn’t blame them. It did sound strange. But I believed in it.

So I turned to the only person who would listen , my brother.  
He was also a student, but he believed in me. He didn’t have much, but he gave me part of his own bourse, no questions asked.

That was real. That was emotional. That gave me purpose.  
It wasn’t just about Ethereum anymore , it was about proving to myself (and to him) that I could do something with nothing.  
That belief powered my rig more than any PSU could.

I didn’t want to waste that help.  
I wanted to build something , something loud, hot, and magical.

---

### Building My First Rig in a Garage

One Saturday, I took over the garage. I didn’t have a frame or tools , just some wood, screws, and a dream.  
I spent the whole day drilling, aligning, adjusting.

By evening, I had it:  
A homemade mining rig with two **RX 580s**, my loyal **GTX 1650**, and a basic PSU.  
I turned it on and watched it boot into life.

When I saw the hash rate spike and Ethereum start trickling into my wallet , I smiled like a madman.  
I wasn’t just watching YouTube anymore.  
**I was mining!**

---

### The Farm Grows: RTX Era Begins

After that, it escalated.

I sold some gadgets, skipped outings, and bought **a third GPU**, then a fourth.  
Eventually, I built up to a real rig , not just one **RTX 3070**, but **three of them**, plus **multiple 3070 Ti** and **3080** cards.  
My setup became a monster.

The buzz of the fans was now the soundtrack of my room.  
The wall socket was nearly smoking.  
My BIOS was tuned tighter than my class schedule.

I was managing overclocks, voltages, and Linux mining OS images like a one-man data center.

---

### The Crash: Low Hashrate Cards and the Merge

But then, things started to shift.  
GPU prices rose. New cards had **LHR (Low Hashrate)** restrictions.  
Electricity became expensive.  
Profit margins shrank.

Then came the final blow: **Ethereum’s Merge** , a shift from Proof of Work to Proof of Stake.  
No more mining. Just like that.

I remember staring at my rig, still powered on, but now doing… nothing.  
The silence was deafening.

---

### What I Gained Was Much More Than Crypto

But you know what? I don’t regret a single watt.

I learned:
- How to manage hardware under pressure  
- How to automate, monitor, cool, and fix  
- How to take risks  
- How to build something with nothing  

I learned **resilience**.

---

### 😄 Bonus: If You're in Tunisia and Want a GPU...

Let’s just say I’ve got a few veterans in the corner , some **RX 580s**, some **RTX 30xx** , just waiting for a new mission.

**If you're serious, and you're reading this instead of scrolling TikTok, maybe I’ll let you adopt one.**  
But only if you promise to mine (or game) **responsibly** 😉.

---

### From Hashes to Charts: My Next Chapter

Mining might be nearly impossible now. The profits are gone, and the difficulty is up.  
But what I gained wasn’t just coins , it was capital, experience, and mindset.

I reinvested part of my mining earnings into something new:  
**Trading.**

With the same curiosity I had for overclocks and fan speeds, I dove into candlestick charts, volume analysis, and crypto strategy.

But that’s another story.  
And if you're curious how I turned my mining gains into trading growth ,  
👉 **You’ll find out in my next blog.**
`
,
      "keywords": "Ethereum mining, GPU mining rig, RX 580 mining, RTX 3080 mining, how to mine Ethereum, student mining Ethereum, crypto rig Tunisia, mining Ethereum before the Merge, building a mining rig, GTX 1650 mining test, passive income with GPU, Ethereum Proof of Work, mining story, mining garage setup, crypto journey student"

      },

      {
        "id": "First_Drift_BMW_E90",
        "title": "How I Bought My E90 and Learned to Drift , One Roundabout at a Time",
        "date": "2025",
        "summary": "In the summer of 2024, I bought my first BMW E90. From basic repairs to my first night drift, this is the personal story of how I fell in love with this car, faced fears like engine burnout, and lived unforgettable moments in empty roundabouts under the streetlights.",
        "type": "personal",
        "fullContent": `In the **summer** of 2024, *something* unexpected happened. One of my old friends, the kind of guy who's always buying, fixing, and reselling cars, had a *BMW E90* he was working on. The car wasn’t in perfect condition, but it had potential. And honestly, it looked *cool*.

He was already repairing the rear differential, replacing the suspension arms on both sides, and redoing the interior roof lining. The body needed love, but the soul of the car was there , and it was *BMW*. That was enough for me to feel something spark.

We talked. I negotiated. And in the end, I bought it.

It wasn’t just a car deal. It was the end of borrowing my parents’ Polo and the beginning of *owning* something real. A jump from economy to power, from plastic feel to that smooth German engine. The driving comfort, the power, the weight , everything felt like an upgrade. I couldn’t stop smiling the first time I sat behind the wheel.

---

### Step One: Bring It Back to Life

I didn’t waste time. I started with all the essentials , the things every smart buyer does when picking up a used car, especially one with a story.

I changed:
- Engine oil  
- Transmission fluid  
- Differential oil  
- Air filters, oil filters, cabin filters  
- Spark plugs  
- Fuel filter  

Just the basics, but it already felt like my own project. Every replaced part made me trust it a little more.

---

### Step Two: Drift Dreams

Now, who buys a *BMW* and doesn’t think , even *just a little* , about speed? Or drifting?  
*BMW* + rear-wheel drive + empty roundabout = temptation.

I started watching tutorials, reading articles, and trying to understand how drifting even works. First you think it’s just acceleration and steering. But then you learn about weight transfer, handbrake techniques, throttle control, and , most importantly , how dangerous it can be if you’re careless.

---

### My First Drift, Alone, at Night

One night, after finishing work late, I was driving home. It was around midnight. The roads were empty. I was completely alone, except for a few stray dogs playing at the edge of the street.

And then I saw it , a perfectly empty roundabout.

I slowed down. My heart started beating fast. This was it.

First try: I shifted into 1st, accelerated, flicked the wheel , and… nothing. Just a weird slow turn. No slide. No magic.

Second try: same story. I almost gave up.  
Then I realized something.

I forgot to disable the DSC.

I reached for the button, clicked it off. And suddenly… full control.  
I took a breath, gave it one more shot.

I accelerated harder, turned in sharply, and *there it was* ,  
the rear came loose, the tires squealed, and for the first time in my life…  
**I was drifting**.

Just a small circle, but my heart was pounding like never before.  
I went back home right after. I couldn’t sleep. I had a permanent smile on my face.

---

### Practice, Progress, and One Fear

Every day after work, I drive home late. It’s not by accident , I like finishing late. The roads are quiet, the streets almost empty. Just me, my *BMW*, and a playlist that matches my mood.

On that path home, I always pass a roundabout in Montplaisir , a famous one. If you’re from around here and you’re a *BMW guy*, you know it. That roundabout *always* has fresh tire marks. It’s like a secret meeting point for ghosts of drifts past.

One night, as I passed through it, something happened.

**A blue E87** appeared behind me. We didn’t talk. We didn’t wave. We just… understood.  
We circled once, then again. Soon we were drifting together, side by side, each of us sliding around the other in perfect rhythm.

Strangers. No names. Just two BMers in sync. And when it ended, we drove off, no words exchanged. He turned left. I turned right. That was it.

But something in me said: *"One more drift before home."*  
So I went back. Lined up again.  
Turned off the DSC. Took a breath.

**Boom.**

A loud sound came from the front. My heart stopped.  
I braked, stopped in the middle of the roundabout. Cut the engine.

No one around. Just a flickering street lamp above me that kept turning on and off , as if to add more fear to the scene.  
I stepped out. Touched the tires. Looked under the car. No oil. No smoke.

I opened the hood. Everything looked… okay. I didn’t want to believe it, but I was terrified.

I turned the key. Engine on.  
**The noise came back.**

It wasn’t knocking. Not metal-on-metal.  
But loud. Ugly. Rapid.

I stepped out again, hood still open, engine running , and there it was:  
The **serpentine belt** had shredded itself. One side of it was tearing off, flapping wildly. That’s what made the sound.

I could finally breathe. The engine was safe.

All I had to do was cut the loose strip and drive the 1–2 kilometers home.  
Small problem: I had no tools. Left them in the garage. Classic.

I looked around for anything , but nothing.  
And then…

A **black E90** drifted into the roundabout , loud, fast, clean. A guy and his girl inside. He saw me, stopped, rolled down the window. I explained.  
He searched his car , nothing useful.

But he said, “Wait, I’ll find something.”

Five minutes later, he returned… with a **disposable razor** from a shop.  
He handed it to me with a smile.

And yes, that little razor fixed my night.

Since then, I’ve become a lot more careful. I drift better, smoother, more in control. I’ve learned how to feel the weight, not just throw it.

But even now, if I’m at a roundabout, DSC off, and ready to slide ,  
I still remember that night, that sound, and that razor.

Now, when I see an empty roundabout, and I know the DSC is off…  
I can’t lie. I’m probably doing a turn or two before I exit.

So yes, I drift. I enjoy it. But I always listen to the engine, watch the temps, and never push her when she’s not ready.

And I always check my belt before I drift (Well… I like to say that. In reality, I usually forget, then pray mid-slide that nothing explodes. So no, this is not professional advice , it’s just a guy hoping his engine doesn’t throw a tantrum while he's having fun.)

---

### From Polo to E90 , A Different World

It’s been less than a year since I bought that car, but it’s already part of my story.  
More than a vehicle, it’s a project. A teacher. A thrill machine.

I don’t regret a single second.

Except maybe that time when I drifted and saw police headlights behind me.  
Well… that’s a whole different story.  
But that’s for another blog.`,

      "keywords": "BMW E90, drift story, beginner drift, Montplaisir roundabout, engine maintenance, PDC BMW, belt failure, first car drift, Tunis car scene, E90 modifications, how to drift BMW, DSC BMW, burnout fear, street drifting, belt noise BMW, real BMW stories",

      },
      
      {
        id: "Install_INPA_NCS_Expert",
        title: "How I Installed INPA & NCS Expert to Code My BMW E90 (and Solved the First Headaches)",
        type: "personal",
        date: "2025",
        summary: "A hands-on guide on how I installed BMW diagnostic software (INPA & NCS Expert) on Windows to code hidden features on my E90, including real issues I faced, and how you can get the working setup.",
        fullContent: `
      If you’re like me, a BMW E90 owner who loves fixing things yourself, you’ve probably heard about coding options like mirror folding, cornering fog lights, or deactivating seatbelt warnings.
      
      To do that, you need BMW diagnostic software like **INPA**, **NCS Expert**, **WinKFP**, and **Tool32**. But the truth is: it’s not as plug-and-play as you think. Here's how I got mine working, what problems I faced, and how you can get a working setup too.
      
      ---
      
      ### 🛠️ What Tools You Need
      
      - A **Windows laptop** (Win 7 or Win 10 64-bit recommended)
      - A **K+DCAN cable** (USB OBD2 interface, not expensive)
      - **INPA/NCS Expert software**
      - Patience 😅
      
      > ❗ **Note:** Took me a while to dig up the right files and working versions. Most of the links out there are either broken, sketchy, or missing pieces. If you need the installer, just hit me up and I’ll send you the exact one I use , **totally free, no strings attached (not even a follow 😄).**
      
      ---
      
      ### 💻 Installation Steps (Simplified)
      
      #### 1️⃣ Use a Clean Windows System
      - Preferably a fresh Windows 10 or Windows 7 (x64)
      - Disable driver signature enforcement (if needed)
      - Run installer **as Administrator**
      
      #### 2️⃣ Install INPA/NCS Expert Package
      - Install the base package (usually called BMW Standard Tools)
      - It includes INPA, NCS Expert, WinKFP, Tool32
      
      Make sure the install path is:
      
      \'\'\'
      C:\\EDIABAS
      C:\\INPA
      C:\\NCSEXPER
      \'\'\'
      
      #### 3️⃣ Set Environment Variables
      Add to your system \'PATH\':
      
      \'\'\'
      C:\\EDIABAS\\BIN
      C:\\NCSEXPER\\BIN
      \'\'\'
      
      Also, make sure your \'EDIABAS.INI\' looks like this:
      
      \'\'\'
      Interface=STD:OBD
      \'\'\'
      
      #### 4️⃣ Install Drivers for K+DCAN Cable
      - Install the FTDI driver (if needed)
      - Check Device Manager:
        - COM Port should show (e.g., COM5)
        - Baud rate: 9600 or 115200 depending on your car and cable
      
      #### 5️⃣ Test Connection with INPA
      - Run \'INPA.exe\'
      - Turn the key to ignition (not engine start)
      - Dots should be **black**, not white
      - Select E90 and test any module (e.g., airbag, engine)
      
      ---
      
      ### ❌ Problems I Faced
      
      - Took **hours to find a full working installer**
      - Some versions had missing files or wrong language
      - My COM port was incorrect , INPA didn’t detect the car
      - Needed to **set compatibility to Win XP SP3** for some EXEs
      - NCS Expert had missing \'.TRC\' file when I first tried reading an ECU
      
      ---
      
      ### ✅ How I Fixed It
      
      - Reinstalled using a **clean full package** (I now keep a backup ZIP)
      - Adjusted COM port to match \'OBD.ini\'
      - Made sure the \'datens\' folder matched my E90 (important for coding)
      - Installed everything as Admin and avoided installing under \'Program Files\'
      
      ---
      
      ### 📩 Want My Version?
      
      If you're stuck like I was, feel free to message me. I’ll send you the exact **working INPA/NCS Expert setup** I use for my E90. It includes drivers, install guide, and tested compatibility.
      
      ---
      
      ### 🚗 Next Step
      
      Now that the software works, I’ll show you how I:
      - Read my E90’s modules
      - Activated hidden options (mirror fold, fog cornering)
      - Solved real coding issues (VO problems, modules not showing)
      
      > Stay tuned for part 2 , **BMW E90: Activating Hidden Options via NCS Expert** 🔧
      `,
        keywords: "BMW PDC coding, E90 sensors, front rear parking assist, BMW E90 INPA install, NCS Expert installation, BMW Standard Tools, INPA Windows 10, Ediabas setup, K+DCAN cable, how to install NCS Expert, BMW coding software setup, BMW E90 coding guide, INPA and NCS Expert tutorial, BMW hidden features, NCS Expert beginner, mirror folding BMW, activate DRL E90, seatbelt chime coding, BMW K+DCAN guide, BMW E90 airbag light fix, INPA airbag error, BMW diagnostic tools, INPA Fehler löschen, Zündkreis Fehler BMW, battery terminal coding error, BMW dashboard warning fix, BMW E90 PDC repair, parking sensor coding, BMW front sensor activation, NCS Expert PDC visual, BMW CCC PDC not showing, fix PDC front sensors BMW, INPA PDC diagnostic"
      }
      ,

      {
        id: "INPA_NCS_Expert",
        title: "Understanding INPA and NCS Expert: My First Steps into BMW Coding (And Why It Was Totally Worth It)",
        type: "personal",
        date: "2025",
        summary: "If you've ever wondered how people unlock hidden features in their BMWs from home, here's how I personally discovered, installed, and started using INPA and NCS Expert, with all the beginner doubts, obstacles, and discoveries that came with it.",
        fullContent: `
      I remember the first time I saw someone on YouTube coding their BMW E90. They pressed a button, and boom , the mirrors folded automatically. Another guy activated the fog lights to turn with the steering. I thought, “What the hell is this wizardry?!”
      
      I had to try it myself. But I quickly realized there’s a whole hidden world behind that magic , a world called INPA and NCS Expert.
      
      Let me explain what I learned. Not as an expert , just as a guy who tried, failed, retried, and finally got it working.
      
      ---
      
      ### 🧠 What Are INPA and NCS Expert, Really?
      
      If you search online, you'll find pages that say “BMW diagnostic tool” or “ECU coding software,” but those don’t help if you’ve never used them.
      
      Let me put it simply:
      
      - **INPA** is your **BMW’s health monitor**. It tells you what’s going on inside the car: engine, airbag, ABS, doors, lights. You can check error codes, reset them, and even see live data like RPMs, temperatures, or brake sensors.
      
      - **NCS Expert** is the **remote control** that lets you actually change how the car behaves. Want the mirrors to fold with the remote? Want DRLs always on? Want to disable the seatbelt beep? That’s NCS Expert.
      
      Together, these tools give you something most garages won’t: full access to your BMW's brain.
      
      ---
      
      ### 🧩 What Scared Me at First (And What Helped)
      
      When I started, I had three big fears:
      
      1. **“What if I brick the car?”** Honestly, it’s unlikely. As long as you follow the right steps and don’t write random values, it’s safe. But yeah , double-check every line before writing.
      
      2. **“Where do I get the software?”** That was a nightmare. Most links were broken, some gave me partial installations, and others were shady as hell. If you're reading this and need it , seriously, contact me and I’ll send you the full, clean setup I use.
      
      3. **“Is it even worth it?”** Oh yes. Once I activated my first hidden feature and it worked, I was hooked. It feels like unlocking cheat codes in real life.
      
      ---
      
      ### 💻 What You'll Actually Be Doing
      
      To use INPA or NCS Expert, here’s the general idea:
      
      1. **Get the software working on Windows** (not always easy , I’ll show you how in the next blog)  
      2. **Connect your car using a K+DCAN cable** (USB cable, around 15€)  
      3. **Use INPA to scan modules**, clear errors, or watch live data  
      4. **Use NCS Expert to read and code specific options** on each ECU  
      
      The process is old-school. The software looks like it came from Windows 95. Some menus are in German. But once you get the rhythm, it’s addictively powerful.
      
      ---
      
      ### 🔧 The First Time I Used INPA
      
      The first thing I did was plug in the cable, turn the key to ignition, open INPA, and… nothing.
      
      I had white dots instead of black.
      
      I learned that:
      
      - The COM port on my cable was wrong (had to change it to COM1 or COM3)  
      - I needed to run INPA as Administrator  
      - My laptop had driver signature enforcement on (disabled that)  
      
      Once it worked, I could actually read modules like **AIRBAG**, **ENGINE**, and **KOMBI**. I felt like a hacker. 😄
      
      ---
      
      ### 🧠 And NCS Expert?
      
      NCS Expert took more time. It’s not beginner-friendly. There are terms like FA, VO, ZCS, FSW_PSW…
      
      But here’s what I figured out:
      
      - You load your car’s profile (E90, E91, etc.)  
      - Read a module like CAS or LCM  
      - Extract the configuration (\'.TRC\' file)  
      - Modify it safely  
      - Save it and write it back to the car  
      
      That’s how I later activated fog lights in turns, auto mirror fold, and other fun things I’ll explain in the next part.
      
      ---
      
      ### 🤝 Final Thoughts Before Installing
      
      This blog isn’t a tutorial , it’s a diary.  
      I wanted to share what INPA and NCS Expert are, in normal words, because too many guides online jump straight into complicated steps.
      
      So if you’re starting your BMW coding journey:  
      - Be patient  
      - Take your time  
      - Don’t panic when things go wrong  
      - And don’t hesitate to ask , I’ve been there.
      
      > The car is smarter than we think , but now, so are we. 😉
      
      Next up: **Installing INPA/NCS Expert on Windows (Step-by-Step)** + where to get the software if you’re stuck.
      `,
        keywords: "BMW PDC coding, E90 sensors, front rear parking assist, BMW E90 INPA install, NCS Expert installation, BMW Standard Tools, INPA Windows 10, Ediabas setup, K+DCAN cable, how to install NCS Expert, BMW coding software setup, BMW E90 coding guide, INPA and NCS Expert tutorial, BMW hidden features, NCS Expert beginner, mirror folding BMW, activate DRL E90, seatbelt chime coding, BMW K+DCAN guide, BMW E90 airbag light fix, INPA airbag error, BMW diagnostic tools, INPA Fehler löschen, Zündkreis Fehler BMW, battery terminal coding error, BMW dashboard warning fix, BMW E90 PDC repair, parking sensor coding, BMW front sensor activation, NCS Expert PDC visual, BMW CCC PDC not showing, fix PDC front sensors BMW, INPA PDC diagnostic"
      }
      ,
      
      {
        "id": "BMW_Diagnostic_with_INPA",
        "title": "My First BMW Diagnostic with INPA: How I Detected and Deleted the Airbag Error Caused by a Loose Battery Terminal",
        "type": "personal",
        "date": "2025",
        "summary": "This is how I used INPA for the first time to diagnose and fix an airbag warning light on my BMW E90, caused by a loose negative battery terminal. A true story from a real beginner who wanted to take control over his car’s electronics.",
        "fullContent": `
    The moment that red airbag light came on, I felt my stomach drop.
    
    Anyone who’s owned a BMW E90 knows that dreaded symbol. It screams **"expensive problem"**, and worse , it’s tied to your car’s safety systems. Was it a sensor? A real fault? Something I broke? My brain went wild.
    
    But instead of rushing to a garage or panicking, I took a breath. I remembered I had just installed **INPA** the week before. This was it , the perfect chance to test it out.
    
    ---
    
    ### ⚠️ The Symptom
    
    It happened on a calm morning. I turned the key, and suddenly:  
    **Red airbag light. Constant. Solid.**
    
    - No crash  
    - No seats removed  
    - No unplugged airbags  
    
    But one thing I did do a few days before? I changed the **battery**. That was my only recent intervention.
    
    ---
    
    ### 🧩 The Real Culprit
    
    After thinking it over, something clicked.
    
    When I replaced the battery, I remembered that the **negative terminal** wasn’t sitting as tight as it should. I thought it was fine , just a clamp, right?  
    Big mistake.
    
    Turns out, when the battery connection is loose , even slightly , it can cause voltage dips and signal noise. The airbag module interprets that as a fault. And BAM, error code.
    
    ---
    
    ### 💻 Connecting INPA for the First Time
    
    I had installed **BMW Standard Tools** the previous weekend. Here's what I did:
    
    1. Plugged in my **K+DCAN** cable to the laptop and OBD2 port  
    2. Turned the key to **ignition on** (not engine start)  
    3. Opened **INPA** (as admin)  
    4. Saw those beautiful **two black dots** ✅
    
    INPA was talking to my E90. I felt like I just hacked into NASA.
    
    ---
    
    ### 🔍 Reading the Error
    
    I selected:  
    **E90 > AIRBAG module**
    
    And there it was:
    
    Fehlercode: 93A8
    Fehlertext: Zündkreis Beifahrerairbag – Widerstand zu groß,
      
    Translation?  
    *Passenger airbag ignition circuit – resistance too high.*

    So INPA confirmed what I was starting to suspect:  
    The system was detecting **unstable voltage** or **poor contact** in the airbag's wiring circuit , likely caused by the battery terminal.

    ---

    ### 🛠 The Fix: Old School and Simple

    I headed to the trunk.  
    Opened the battery cover.  

    I tried wiggling the **negative terminal** , and yes, it was **loose**.  
    Two fingers were enough to move it. Definitely not okay.

    I grabbed a wrench, tightened the clamp properly, and double-checked the connection.

    Back to the driver’s seat.

    ---

    ### ❌ Deleting the Error

    Inside INPA again:

    - Still in the AIRBAG module  
    - Selected **Fehler löschen** (delete error)  
    - Got a confirmation

    A few seconds later, I turned the key...  
    **No more red light**. No more stress. Just quiet dashboard bliss.

    ---

    ### ❤️ What I Learned from That Day

    That little diagnostic changed how I saw my car , and myself.

    - The car *talks*, if you listen the right way  
    - You don’t need to be a pro to fix small things  
    - A €15 cable and curiosity are sometimes all you need

    It wasn’t just about deleting a fault.  
    It was about learning, growing, and realizing that BMW electronics aren’t a black box.

    ---

    ### 🔧 Why You Should Try It Too

    - Garages would’ve charged me at least €80 just to read and clear that code  
    - I fixed it with **one tool** and **10 minutes**  
    - And now I feel way more confident working on my car

    > So if you see a light, don’t panic , plug in, read, understand.

    ---

    ### 🗺️ What’s Next?

    That moment pushed me further into the world of diagnostics and coding.

    In my next blog, I’ll show how I used **NCS Expert** to activate real hidden features like:

    - Mirror fold with key  
    - Fog lights turning with the steering  
    - Daytime running lights (DRL)

    The tools are free. The experience is priceless.  
    See you in the next one 😉
    `,
    "keywords": "BMW E90 airbag light fix, INPA airbag error, INPA Fehler löschen, Zündkreis Fehler BMW, battery terminal coding error, BMW diagnostic tools, BMW K+DCAN, E90 diagnostic with INPA, BMW dashboard warning fix, how to delete airbag code BMW, fix airbag light BMW E90, INPA code 93A8, BMW DIY fix, BMW loose battery terminal, BMW OBD2 coding"
    }

,
{
    id: "BMW_PDC_FIXING",
    title: "Fixing My BMW E90’s PDC: From No Sensors to Full Front + Rear Visual Display",
    type: "personal",
    date: "2025",
    summary: "When I bought my E90, the parking sensors didn’t work at all. I diagnosed the issue with INPA, replaced 2 broken front sensors, and then activated full front + rear PDC visual display on the CCC screen using NCS Expert.",
    fullContent: `
  When I bought my BMW E90, the parking sensors didn’t work. At all. No beep, no screen, nothing. Honestly, I thought the car didn’t even have PDC.
  
  But I had a feeling. So I decided to dig deeper, and that’s when this whole repair adventure started. Here’s how I brought the system back to life, step by step, until it finally showed the full front + rear view on the iDrive screen.
  
  ---
  
  ### 🔎 Step 1: Diagnosing with INPA
  
  First thing I did was connect my laptop and launch **INPA**.
  
  - I selected the **PDC module**
  - Read the error memory (Fehlerspeicher lesen)
  - And saw two clear faults:
  
   
  Sensor vorne links – kein Signal  
  Sensor vorne rechts – Kurzschluss  
   
  
  So the car *did* have front sensors, but both front corners were dead.
  
  ---
  
  ### 🔧 Step 2: Replacing the Sensors
  
  I removed the front bumper and confirmed it visually: both sensors were either corroded or unplugged.
  
  I bought two new ones (painted to match the bumper), plugged them in, and cleared the errors in INPA.
  
  I tested again, and this time, I got **no faults** ✅
  
  I pressed the PDC button in the car… and finally heard the **beeping from the rear sensors**.
  
  Progress! But there was still a problem…
  
  ---
  
  ### ❓ Rear Sensors Working, But No Front Visual?
  
  Even though all sensors were active, the **CCC screen only showed rear PDC display**. The front sensors were completely ignored in the visual overlay.
  
  I knew they were wired and active, but clearly, something had to be **activated in the software**.
  
  That’s when I turned to **NCS Expert**.
  
  ---
  
  ### 💻 Step 3: Activating Full PDC Display (Front + Rear)
  
  Here’s exactly how I coded it:
  
  #### What you need:
  - Working **NCS Expert** installation  
  - **K+DCAN cable**  
  - Car with **CCC unit** (like mine)  
  #### What you need:

- Working **NCS Expert** installation  
- **K+DCAN cable**  
- Car with **CCC unit** (like mine)  
- Profile that allows editing '.MAN' files ('Revtor' or 'Expertmode')

#### Steps:

1. Open **NCS Expert** as Administrator  
2. Load profile: 'Revtor's NCS Expert' (or similar)  
3. Click F1 > F3  
   - Select chassis: 'E89'  
   - Choose module: 'CAS'  
4. Click F6 (Back) > F4 (Process ECU)  
   - Select ECU: 'CAPPL' (handles CCC visual)  
5. Click F5 (Read ECU) , this will read your config and create FSW_PSW.TRC  
6. Navigate to 'C:\\NCSEXPER\\WORK'  
   - Rename 'FSW_PSW.TRC' to 'FSW_PSW.MAN'  
   - Open it with Notepad

#### What to modify:

Replace these lines inside the file:

PDC_DISPLAY = aktiv  
PDC_VORNE = aktiv

*(If you find 'nicht_aktiv', just replace it with 'aktiv')*

7. Back in NCS Expert:  
   - Click F2 (Change Job) > choose 'SG_CODIEREN'  
   - Click F3 (Execute Job)

Wait a few seconds. You should see: “Coding ended.” ✅

  ### 🧪 The Result: Full Front + Rear Display
  
  Now when I press the PDC button, I see:  
  - Visual lines front **and** rear on the screen  
  - Sensors respond when I get close to objects  
  - The system finally works like it should have from the beginning
  
  ---
  
  ### ✅ Final Thoughts
  
  It started with **no PDC at all**. Then I diagnosed it myself, changed the broken sensors, and finished by unlocking a feature that was hidden the whole time.
  
  And honestly? It was way easier than people think.
  
  > If you’re in the same situation, don’t give up , the car already has what it needs. It just needs someone like you to wake it up.
  
  Next time, I’ll show how I activated **cornering fog lights** and **mirror folding**, and how I recovered from a mistake that almost bricked my FRM module 😅
  `
  ,    "keywords": "BMW PDC coding, E90 sensors, front rear parking assist, BMW E90 INPA install, NCS Expert installation, BMW Standard Tools, INPA Windows 10, Ediabas setup, K+DCAN cable, how to install NCS Expert, BMW coding software setup, BMW E90 coding guide, INPA and NCS Expert tutorial, BMW hidden features, NCS Expert beginner, mirror folding BMW, activate DRL E90, seatbelt chime coding, BMW K+DCAN guide, BMW E90 airbag light fix, INPA airbag error, BMW diagnostic tools, INPA Fehler löschen, Zündkreis Fehler BMW, battery terminal coding error, BMW dashboard warning fix, BMW E90 PDC repair, parking sensor coding, BMW front sensor activation, NCS Expert PDC visual, BMW CCC PDC not showing, fix PDC front sensors BMW, INPA PDC diagnostic"

  },
      

      
      
      
{
  "id": "club-africain",
  "title": "🛑 الرئاسة مش للبيع.. النادي الإفريقي للتوانسة 🇹🇳‼️",
  "type": "sport",
  "date": "2025",
  "summary": "خسارة ثقيلة، غضب جماهيري، وتكهنات حول ترشح مستثمر أمريكي للرئاسة.. فهل يباع النادي الإفريقي؟ أم تبقى الرئاسة تونسية؟",
  "image":"https://www.raedmadouri.com/ca.jpg",
  "fullContent": `
  ![رئيس النادي الإفريقي](https://www.raedmadouri.com/ca.jpg)

📉 هزيمة ثقيلة جديدة ضد الاتحاد المنستيري (3-0)
🚨 الفريق خارج سباق اللقب والمراكز الإفريقية
😡 الجماهير غاضبة.. تطالب برحيل الإدارة

🗣️ فيرجي شامبيرز، المستثمر الأمريكي،
نشر تدوينات غامضة بعد الخسائر
قال باش “يقرّر” ويحمّل المسؤوليات 👀

📢 البعض يقول: "يريد يترشح للرئاسة!"
⚠️ والبعض الآخر: "حاب يملك النادي!"

🧠 لكن الحقيقة إلّي لازم الكل يعرفها:
النادي الإفريقي ماهوش للبيع
و الرئاسة لازمها تبقى تونسية 🇹🇳

🔴 الإفريقي رمز.. تاريخ.. وهوية
ما نجموش نسيبوه في يد الأجانب، مهما كانت نواياهم
  `,
  "keywords": "النادي الإفريقي, رئيس النادي الإفريقي, أخبار الرياضة في تونس, كرة القدم التونسية, الرابطة المحترفة الأولى, تغيير إدارة النادي الإفريقي"
}
,



{
  "id": "presidence-ca-2025",
  "title": "🏟️ من سيقود الإفريقي؟ صراع رئاسي ينحصر بين اسمين 🔴⚪️",
  "type": "sport",
  "date": "2025",
  "summary": "🏟️ منصب رئيس النادي الإفريقي ينحصر بين هذا الثنائي… من يقود الفريق في المرحلة القادمة؟ 🔴⚪️",
  "image": "https://www.raedmadouri.com/ca2.jpg",
  "fullContent": `
![صراع الرئاسة في النادي الإفريقي](https://www.raedmadouri.com/ca2.jpg)

مع استقالة الهيئة المديرة واقتراب موعد الجلسة العامة الانتخابية، بدأت تتوضح ملامح الصراع على رئاسة النادي الإفريقي.

لكن المفاجأة؟ الجماهير تبدو متّفقة على شي واحد: اسمين فقط يستحقوا قيادة المرحلة القادمة.

👀 الكل يستنى. الكل يراقب. من سيتسلّم الدفّة في الحديقة "أ"؟

⚖️ بين السليمي وإيدير… جمهور الإفريقي اختار الثنائي

الوضع الإداري، المالي، والرياضي صعب.
والتحديات كثيرة.

في خضم هذا الكل، الجمهور يركّز على اسمين بارزين:

👤 عادل السليمي… نجم الميدان المرشّح للمكتب؟

عادل السليمي معروف عند كل محبّ للإفريقي.
لاعب دولي سابق. مدرّب سابق. يعرف الدار من الداخل.

الناس تشوف فيه رمزًا. رجل مرحلة.
يملك شعبية كبيرة بين الجماهير ❤️

لكن بعض الأصوات تخاف من حاجة: هل عنده الخبرة الكافية في التسيير الإداري؟ 🧐

🧠 كمال إيدير… رجل الخبرة والهدوء؟

من الجهة الثانية، يبرز كمال إيدير.

إيدير عنده تجربة طويلة.
شارك في هيئات سابقة. يعرف دواليب التسيير.

يشوفوه الناس "الحل الآمن".
شخص يقدر يرجّع التوازن للنادي.
ويطرح رؤية طويلة المدى.

مدعوم من بعض الأسماء النافذة.
ويحظى بثقة شريحة كبيرة من الجمهور.

🤔 من الأصلح؟ ومن الأقرب؟

رغم عدم صدور ترشيحات رسمية، الكواليس تحسم الأمر: الصراع انحصر بين السليمي وإيدير.

جلسة عامة حاسمة في الطريق.
والمصير سيتقرر قريبًا ⏳

✅ الخلاصة

جمهور الإفريقي اختار.
السليمي أو إيدير… لا ثالث لهما.

المرحلة القادمة مصيرية.
والرئيس الجديد مش مجرّد اسم.
بل فرصة جديدة… وبداية جديدة. 🔄

القرار عند أصحاب الأصوات.
لكن التاريخ هو اللي باش يقيّم القرار. 📚⚖️
`
}

      
       
      
      
      
      
];
  